/**
 * AdminOrders.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import { getIcon } from '../utils/icons';
import LoadingSpinner from './LoadingSpinner';
import { SITE_TAGLINE_SHORT, SITE_TAGLINE_WITH_DASH } from '../constants/siteTagline';
import OrderStatusTimeline from './OrderStatusTimeline';
import OrderCollapsibleSection from './OrderCollapsibleSection';
import { parseInquiryThread } from '../utils/orderHistoryDisplay';
import AdminMailOrderModal from './AdminMailOrderModal';
import { sendMailOrderInvoiceEmail } from '../utils/mailOrderInvoiceEmail';
import { MAIL_ORDER_ACTION_LABEL } from '../utils/mailOrderConstants';
import { buildMailOrderDraftFromCartInquiry, formatAddressForMailOrder } from '../utils/mailOrderDraft';
import './AdminOrders.css';
import './OrderCollapsibleSection.css';

import {
  formatDateAtlantic,
  formatDateOnlyAtlantic,
  formatTimeOnlyAtlantic,
  formatOrderDate,
  formatOrderDateForCsv,
  parseOrderDateFromCsv,
} from '../utils/dateFormat';
import {
  resolveOrderStripeMode,
  resolveOrderPayPalMode,
  stripePaymentDetailUrl,
  stripePaymentsListUrl,
  stripeDashboardHomeUrl,
  stripeDashboardSearchUrl,
  isStripePaymentMethod,
  isPayPalPaymentMethod,
  paypalTransactionUrl,
  paypalActivityUrl,
  formatOrderPaymentLabel,
  getOrderPaymentEnvBadge,
  getOrderEnvBadgeStyles,
} from '../utils/stripeDashboard';
import {
  buildRefundExplanationForReason,
  formatRefundAmountLabel,
  syncRefundAmountInExplanation,
} from '../utils/refundReasonTemplates';

const API_URL = '/api/orders';
const ORDERS_EMAIL_ADDRESS = 'orders@cranfordpub.ca';
const ORDERS_EMAIL_FROM = '"Orders Cranford Publications" <orders@cranfordpub.ca>';

const NO_CACHE_HEADERS = {
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  'Pragma': 'no-cache',
};

const fetchNoCache = (url, options = {}) => fetch(url, {
  ...options,
  cache: 'no-store',
  headers: {
    ...NO_CACHE_HEADERS,
    ...options.headers,
  },
});

const ADMIN_SECTION_IDS = {
  CONVERSATION: 'conversation',
  HISTORY: 'history',
};

function getAdminInquiryThread(order) {
  return parseInquiryThread(order?.inquiry_thread);
}

function getCartInquiryReplies(inquiry) {
  if (Array.isArray(inquiry?.replies)) return inquiry.replies;
  try {
    const parsed = JSON.parse(inquiry?.replies_json || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function getAdminDefaultCollapsedSections(order) {
  const hasConversation = getAdminInquiryThread(order).length > 0;
  return {
    [ADMIN_SECTION_IDS.CONVERSATION]: !hasConversation,
    [ADMIN_SECTION_IDS.HISTORY]: true,
  };
}

const AdminOrders = () => {
  const { currentUser, isAdmin } = useAuth();
  const { showSuccess, showError } = useToast();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [cartInquirySortColumn, setCartInquirySortColumn] = useState('date');
  const [cartInquirySortDirection, setCartInquirySortDirection] = useState('desc');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [sendEmailNotification, setSendEmailNotification] = useState(true);
  const [customMessage, setCustomMessage] = useState('');
  const [selectedCannedMessageForStatus, setSelectedCannedMessageForStatus] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [selectedCannedMessage, setSelectedCannedMessage] = useState('');
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('shipping_refund');
  const [refundExplanation, setRefundExplanation] = useState('');
  const [refundExplanationDirty, setRefundExplanationDirty] = useState(false);
  const lastRefundAmountLabelRef = useRef('');
  const [processingRefund, setProcessingRefund] = useState(false);
  const [selectedRefundItems, setSelectedRefundItems] = useState([]);
  const [refundMode, setRefundMode] = useState('amount'); // 'amount' or 'items'
  const [inquiryReply, setInquiryReply] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [paymentBreakdown, setPaymentBreakdown] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [showRefundConfirmModal, setShowRefundConfirmModal] = useState(false);
  const [showRefundSuccessModal, setShowRefundSuccessModal] = useState(false);
  const [refundSuccessResult, setRefundSuccessResult] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [importingOrders, setImportingOrders] = useState(false);
  const [cannedMessages, setCannedMessages] = useState([]);
  const [cannedMessagesLoaded, setCannedMessagesLoaded] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [merchantBalances, setMerchantBalances] = useState(null);
  const [loadingMerchantBalances, setLoadingMerchantBalances] = useState(false);
  const [paymentSummaries, setPaymentSummaries] = useState({});
  const [loadingPaymentSummaries, setLoadingPaymentSummaries] = useState(false);
  const [exportingFees, setExportingFees] = useState(false);
  const [showMailOrderModal, setShowMailOrderModal] = useState(false);
  const [mailOrderDraft, setMailOrderDraft] = useState(null);
  const [cartInquiries, setCartInquiries] = useState([]);
  const [selectedCartInquiry, setSelectedCartInquiry] = useState(null);
  const [showCartInquiryReplyModal, setShowCartInquiryReplyModal] = useState(false);
  const [cartInquiryReplyTarget, setCartInquiryReplyTarget] = useState(null);
  const [cartInquiryReplyMessage, setCartInquiryReplyMessage] = useState('');
  const [cartInquirySendEmail, setCartInquirySendEmail] = useState(true);
  const [selectedCannedMessageForCartInquiry, setSelectedCannedMessageForCartInquiry] = useState('');
  const [sendingCartInquiryReply, setSendingCartInquiryReply] = useState(false);
  const [cartInquiryToDelete, setCartInquiryToDelete] = useState(null);
  const [deletingCartInquiry, setDeletingCartInquiry] = useState(false);
  const [showArchivedCartInquiries, setShowArchivedCartInquiries] = useState(false);
  const [archivingCartInquiryId, setArchivingCartInquiryId] = useState(null);
  const [markingPaymentReceived, setMarkingPaymentReceived] = useState(false);

  const getAdminPaymentHeaders = (extra = {}) => {
    const token = localStorage.getItem('token');
    return {
      ...NO_CACHE_HEADERS,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extra,
    };
  };

  const fetchPaymentBreakdownForOrder = async (order) => {
    if (!order?.payment_intent_id && !order?.paypal_order_id) {
      setPaymentBreakdown(null);
      return;
    }

    setLoadingBreakdown(true);
    setPaymentBreakdown(null);
    try {
      const response = await fetch(`/api/payment/payment-breakdown/${order.id}`, {
        headers: getAdminPaymentHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Payment breakdown HTTP error:', response.status, errorText);
        setPaymentBreakdown(null);
        return;
      }

      const data = await response.json();
      if (data.success && (data.hasPaymentData || data.hasStripeData || data.hasPayPalData) && data.breakdown) {
        setPaymentBreakdown({
          ...data.breakdown,
          provider: data.provider,
          stripeMode: data.stripeMode,
          paypalMode: data.paypalMode,
          stripeDashboardUrl: data.stripeDashboardUrl,
        });
      } else {
        setPaymentBreakdown(null);
      }
    } catch (error) {
      console.error('Error fetching payment breakdown:', error);
      setPaymentBreakdown(null);
    } finally {
      setLoadingBreakdown(false);
    }
  };

  const fetchMerchantBalances = async () => {
    setLoadingMerchantBalances(true);
    try {
      const response = await fetch('/api/payment/merchant-balances', {
        headers: getAdminPaymentHeaders(),
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.success && data.balances) {
        setMerchantBalances(data.balances);
      }
    } catch (error) {
      console.error('Failed to load merchant balances:', error);
    } finally {
      setLoadingMerchantBalances(false);
    }
  };

  const fetchPaymentSummariesForOrders = async (orderList) => {
    const payableOrders = (orderList || []).filter(
      (order) => order.payment_intent_id || order.paypal_order_id
    );
    if (!payableOrders.length) {
      setPaymentSummaries({});
      return;
    }

    const CHUNK_SIZE = 50;
    setLoadingPaymentSummaries(true);
    try {
      const merged = {};
      for (let i = 0; i < payableOrders.length; i += CHUNK_SIZE) {
        const orderIds = payableOrders.slice(i, i + CHUNK_SIZE).map((order) => order.id);
        const response = await fetch('/api/payment/payment-summaries', {
          method: 'POST',
          headers: getAdminPaymentHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ orderIds }),
        });
        if (!response.ok) continue;
        const data = await response.json();
        if (data.success && data.summaries) {
          Object.assign(merged, data.summaries);
        }
      }
      setPaymentSummaries(merged);
    } catch (error) {
      console.error('Failed to load payment summaries:', error);
    } finally {
      setLoadingPaymentSummaries(false);
    }
  };

  const renderMerchantBalanceChip = (label, entry, icon) => {
    if (!entry) return null;
    const unavailable = Boolean(entry.error) || entry.availableFormatted == null;
    const title = entry.error
      ? `${label}: ${entry.error}`
      : `${label} available${entry.pendingFormatted ? ` · pending ${entry.pendingFormatted}` : ''}`;
    return (
      <span
        key={label}
        className={`merchant-balance-chip ${entry.mode === 'sandbox' ? 'sandbox' : 'live'}${unavailable ? ' unavailable' : ''}`}
        title={title}
      >
        <span className="merchant-balance-label">{icon} {label}</span>
        <span className="merchant-balance-value">
          {loadingMerchantBalances && !merchantBalances ? '…' : unavailable ? '—' : entry.availableFormatted}
        </span>
      </span>
    );
  };

  const getAdminSectionKey = (orderId, sectionId) => `${orderId}:${sectionId}`;

  const isAdminSectionCollapsed = (order, sectionId) => {
    if (!order) return true;
    const key = getAdminSectionKey(order.id, sectionId);
    if (Object.prototype.hasOwnProperty.call(collapsedSections, key)) {
      return collapsedSections[key];
    }
    return getAdminDefaultCollapsedSections(order)[sectionId];
  };

  const toggleAdminSection = (order, sectionId) => {
    const key = getAdminSectionKey(order.id, sectionId);
    setCollapsedSections((prev) => ({
      ...prev,
      [key]: !isAdminSectionCollapsed(order, sectionId),
    }));
  };

  // Check permissions
  const canManageOrders = currentUser && currentUser.roles &&
    (currentUser.roles.includes('admin') || currentUser.roles.includes('super_admin'));

  const fetchCannedMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetchNoCache(`${API_URL}/canned-messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load canned message templates');
      const data = await res.json();
      setCannedMessages(Array.isArray(data.messages) ? data.messages : []);
    } catch (err) {
      console.error('Error loading canned messages:', err);
      showError('Could not load canned message templates');
      setCannedMessages([]);
    } finally {
      setCannedMessagesLoaded(true);
    }
  };

  // Format date with time (using Atlantic Time utility)
  const formatDateWithTime = (order) => {
    return formatOrderDate(order.created_at_full || order.created_at);
  };

  // Count displayable items (exclude bundle items that have expanded products)
  const countDisplayableItems = (items) => {
    if (!items || !Array.isArray(items)) return 0;
    return items.filter(item => {
      const isBundle = item.isBundle || item.is_bundle || false;
      const hasBundleProducts = item.bundleProducts && Array.isArray(item.bundleProducts) && item.bundleProducts.length > 0;
      const isPartOfBundle = item.isPartOfBundle || false;
      // Count individual products (isPartOfBundle items)
      if (isPartOfBundle) return true;
      // Count bundle items only if they don't have expanded products
      if (isBundle || hasBundleProducts) {
        const hasExpandedProducts = items.some(i => i.isPartOfBundle && i.bundleParentId === item.id);
        return !hasExpandedProducts;
      }
      return true; // Count standalone items
    }).length;
  };

  // Fetch all orders
  const parseOrderRefunds = (order) => {
    if (!order?.notes_history) return [];
    try {
      const notes = typeof order.notes_history === 'string'
        ? JSON.parse(order.notes_history)
        : order.notes_history;
      return (Array.isArray(notes) ? notes : []).filter((note) => note.type === 'refund');
    } catch {
      return [];
    }
  };

  const getTotalRefunded = (order) => (
    parseOrderRefunds(order).reduce((sum, note) => sum + (note.refundAmount || 0), 0)
  );

  const getSelectedOrders = () => orders.filter((order) => selectedOrderIds.includes(order.id));

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds((prev) => (
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    ));
  };

  const clearOrderSelection = () => setSelectedOrderIds([]);

  const allVisibleOrdersSelected = filteredOrders.length > 0
    && filteredOrders.every((order) => selectedOrderIds.includes(order.id));

  const toggleSelectAllVisibleOrders = () => {
    if (allVisibleOrdersSelected) {
      const visibleIds = new Set(filteredOrders.map((order) => order.id));
      setSelectedOrderIds((prev) => prev.filter((id) => !visibleIds.has(id)));
      return;
    }
    const visibleIds = filteredOrders.map((order) => order.id);
    setSelectedOrderIds((prev) => [...new Set([...prev, ...visibleIds])]);
  };

  const ensureOrderEnriched = async (order) => {
    if (!order?.items) return order;
    const enrichedItems = await enrichOrderItems(order);
    return { ...order, items: enrichedItems };
  };

  const combinePrintDocuments = (htmlDocuments) => {
    const extracted = htmlDocuments.map((html) => {
      const styles = [...html.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)]
        .map((match) => match[1])
        .join('\n');
      const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      const body = bodyMatch ? bodyMatch[1] : html;
      return { styles, body };
    });

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Batch Print</title>
  <style>
    ${extracted.map((content) => content.styles).join('\n')}
    .print-page { page-break-after: always; break-after: page; }
    .print-page:last-child { page-break-after: auto; break-after: auto; }
    @media print {
      .print-page { page-break-after: always; break-after: page; }
      .print-page:last-child { page-break-after: auto; break-after: auto; }
    }
  </style>
</head>
<body>
  ${extracted.map((content) => `<div class="print-page">${content.body}</div>`).join('')}
</body>
</html>`;
  };

  const openPrintWindow = (html, successMessage) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
    showSuccess(successMessage);
  };

  const fetchOrders = async (refreshSelectedOrderId = null, { silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('Not authenticated. Please log in first.');
      }

      const response = await fetchNoCache(API_URL, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch orders');
      }

      const data = await response.json();
      const orderList = data.orders || [];
      setOrders(orderList);
      setFilteredOrders(orderList);

      if (refreshSelectedOrderId) {
        const updated = orderList.find((order) => order.id === refreshSelectedOrderId);
        if (updated) {
          setSelectedOrder((prev) => (
            prev && prev.id === refreshSelectedOrderId
              ? { ...updated, items: prev.items }
              : prev
          ));
        }
      }
    } catch (err) {
      console.error('Fetch orders error:', err);
      showError(err.message);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const fetchCartInquiries = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetchNoCache(`${API_URL}/cart-inquiries/admin`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch cart inquiries');
      }
      const data = await response.json();
      setCartInquiries(data.inquiries || []);
    } catch (err) {
      console.error('Fetch cart inquiries error:', err);
    }
  };

  const openMailOrderFromInquiry = (inquiry) => {
    setSelectedCartInquiry(null);
    setMailOrderDraft(buildMailOrderDraftFromCartInquiry(inquiry));
    setShowMailOrderModal(true);
  };

  const openCartInquiryReply = (inquiry) => {
    setSelectedCartInquiry(null);
    setCartInquiryReplyTarget(inquiry);
    setCartInquiryReplyMessage('');
    setCartInquirySendEmail(true);
    setSelectedCannedMessageForCartInquiry('');
    setShowCartInquiryReplyModal(true);
  };

  const handleCannedMessageSelectForCartInquiry = (cannedId) => {
    if (!cartInquiryReplyTarget || !cannedId) {
      setSelectedCannedMessageForCartInquiry('');
      return;
    }
    const canned = cannedMessages.find((m) => String(m.id) === String(cannedId));
    if (!canned) return;
    let message = canned.message || '';
    message = message.replace(/{customerName}/g, cartInquiryReplyTarget.customer_name || 'Customer');
    message = message.replace(/{orderNumber}/g, cartInquiryReplyTarget.reference_id || '');
    setCartInquiryReplyMessage(message);
    setSelectedCannedMessageForCartInquiry(cannedId);
  };

  const closeCartInquiryReply = () => {
    if (sendingCartInquiryReply) return;
    setShowCartInquiryReplyModal(false);
    setCartInquiryReplyTarget(null);
    setCartInquiryReplyMessage('');
    setCartInquirySendEmail(true);
    setSelectedCannedMessageForCartInquiry('');
  };

  const confirmCartInquiryReply = async () => {
    if (!cartInquiryReplyTarget) return;
    const message = cartInquiryReplyMessage.trim();

    try {
      setSendingCartInquiryReply(true);
      const token = localStorage.getItem('token');
      const response = await fetchNoCache(`${API_URL}/cart-inquiries/${cartInquiryReplyTarget.id}/reply`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sendEmail: cartInquirySendEmail,
          customMessage: message,
          adminName: currentUser?.username || 'Cranford Publications Team',
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || 'Failed to send reply');
      }

      if (result.inquiry) {
        setCartInquiries((prev) => prev.map((row) => (
          row.id === result.inquiry.id ? result.inquiry : row
        )));
      }

      if (cartInquirySendEmail && result.emailSent) {
        showSuccess('Reply sent and the customer was notified by email.');
      } else if (cartInquirySendEmail) {
        showSuccess('Reply saved. The customer email may not have sent.');
      } else {
        showSuccess('Reply saved without emailing the customer.');
      }
      setShowCartInquiryReplyModal(false);
      setCartInquiryReplyTarget(null);
      setCartInquiryReplyMessage('');
      setCartInquirySendEmail(true);
      setSelectedCannedMessageForCartInquiry('');
    } catch (err) {
      console.error('Cart inquiry reply error:', err);
      showError(err.message || 'Failed to send reply');
    } finally {
      setSendingCartInquiryReply(false);
    }
  };

  const confirmDeleteCartInquiry = async () => {
    if (!cartInquiryToDelete) return;
    try {
      setDeletingCartInquiry(true);
      const token = localStorage.getItem('token');
      const response = await fetchNoCache(`${API_URL}/cart-inquiries/${cartInquiryToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete cart inquiry');
      }
      setCartInquiries((prev) => prev.filter((row) => row.id !== cartInquiryToDelete.id));
      if (selectedCartInquiry?.id === cartInquiryToDelete.id) {
        setSelectedCartInquiry(null);
      }
      showSuccess(`Deleted cart inquiry ${cartInquiryToDelete.reference_id}.`);
      setCartInquiryToDelete(null);
    } catch (err) {
      console.error('Delete cart inquiry error:', err);
      showError(err.message || 'Failed to delete cart inquiry');
    } finally {
      setDeletingCartInquiry(false);
    }
  };

  const isCartInquiryArchived = (inquiry) => Boolean(inquiry?.archived);

  const archiveCartInquiry = async (inquiry, archived = true) => {
    if (!inquiry?.id) return;
    setArchivingCartInquiryId(inquiry.id);
    try {
      const response = await fetchNoCache(`${API_URL}/cart-inquiries/${inquiry.id}/archive`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ archived }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || 'Failed to update cart inquiry');
      }
      const updated = result.inquiry || { ...inquiry, archived };
      setCartInquiries((prev) => prev.map((row) => (row.id === inquiry.id ? { ...row, ...updated } : row)));
      setSelectedCartInquiry((current) => (
        current?.id === inquiry.id ? { ...current, ...updated } : current
      ));
      showSuccess(archived
        ? `Archived cart inquiry ${inquiry.reference_id}.`
        : `Restored cart inquiry ${inquiry.reference_id}.`);
    } catch (err) {
      console.error('Archive cart inquiry error:', err);
      showError(err.message || 'Failed to archive cart inquiry');
    } finally {
      setArchivingCartInquiryId(null);
    }
  };

  useEffect(() => {
    if (canManageOrders) {
      fetchOrders();
      fetchCartInquiries();
      fetchCannedMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageOrders]);

  // Check for search parameter in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search');
    if (searchParam) {
      setSearchTerm(searchParam);
      // Optionally scroll to ensure the search box is visible
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (!canManageOrders || !isAdmin()) return;

    const loadPaymentConfig = async () => {
      try {
        const response = await fetch('/api/payment/config', {
          cache: 'no-store',
          headers: getAdminPaymentHeaders(),
        });
        if (!response.ok) return;
        const config = await response.json();
        setPaymentConfig(config);
      } catch (error) {
        console.error('Failed to load payment config for admin orders:', error);
      }
    };

    loadPaymentConfig();
    fetchMerchantBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageOrders]);

  useEffect(() => {
    if (!canManageOrders || !filteredOrders.length) {
      setPaymentSummaries({});
      return;
    }
    fetchPaymentSummariesForOrders(filteredOrders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOrders, canManageOrders]);

  useEffect(() => {
    if (!selectedOrder?.payment_intent_id && !selectedOrder?.paypal_order_id) return;
    if (!showDetailsModal) return;
    fetchPaymentBreakdownForOrder(selectedOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrder?.id, showDetailsModal]);

  // Filter and sort orders based on search, status, date range, and column sort
  useEffect(() => {
    let filtered = orders;

    // Filter by status (support both old and new format)
    if (statusFilter !== 'all') {
      if (statusFilter === 'refunded') {
        // Show both refunded and partially_refunded when refunded filter is selected
        filtered = filtered.filter(order => {
          const paymentStatus = order.payment_status || (order.status === 'refunded' || order.status === 'partially_refunded' ? order.status : null);
          return paymentStatus === 'refunded' || paymentStatus === 'partially_refunded';
        });
      } else {
        // Check both order_status and legacy status field
        filtered = filtered.filter(order => {
          const orderStatus = order.order_status || order.status;
          return orderStatus === statusFilter;
        });
      }
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(order =>
        order.order_number.toLowerCase().includes(search) ||
        order.customer_name.toLowerCase().includes(search) ||
        order.customer_email.toLowerCase().includes(search)
      );
    }

    // Filter by date range
    if (dateRange.from || dateRange.to) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at_full || order.created_at);
        const fromDate = dateRange.from ? new Date(dateRange.from) : null;
        const toDate = dateRange.to ? new Date(dateRange.to + 'T23:59:59') : null;

        if (fromDate && toDate) {
          return orderDate >= fromDate && orderDate <= toDate;
        } else if (fromDate) {
          return orderDate >= fromDate;
        } else if (toDate) {
          return orderDate <= toDate;
        }
        return true;
      });
    }

    const getSortValue = (order) => {
      switch (sortColumn) {
        case 'order_number':
          return String(order.order_number || '').toLowerCase();
        case 'date':
          return new Date(order.created_at_full || order.created_at || 0).getTime() || 0;
        case 'customer':
          return String(order.customer_name || '').toLowerCase();
        case 'items':
          return countDisplayableItems(order.items);
        case 'total':
          return Number(order.total) || 0;
        case 'order_status':
          return String(order.order_status || order.status || '').toLowerCase();
        case 'payment_status':
          return String(order.payment_status || 'paid').toLowerCase();
        default:
          return '';
      }
    };

    const sorted = [...filtered].sort((a, b) => {
      const aVal = getSortValue(a);
      const bVal = getSortValue(b);
      let cmp = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    setFilteredOrders(sorted);
  }, [searchTerm, statusFilter, dateRange, orders, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection(column === 'date' || column === 'total' ? 'desc' : 'asc');
    }
  };

  const handleCartInquirySort = (column) => {
    if (cartInquirySortColumn === column) {
      setCartInquirySortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setCartInquirySortColumn(column);
      setCartInquirySortDirection(column === 'date' || column === 'total' || column === 'items' ? 'desc' : 'asc');
    }
  };

  const renderSortableHeader = (column, label, sortState = null) => {
    const activeColumn = sortState?.column ?? sortColumn;
    const activeDirection = sortState?.direction ?? sortDirection;
    const onSort = sortState?.onSort ?? handleSort;
    const isActive = activeColumn === column;
    const ariaSort = isActive ? (activeDirection === 'asc' ? 'ascending' : 'descending') : 'none';
    return (
      <th
        className={`sortable-th${isActive ? ' sortable-th--active' : ''}`}
        aria-sort={ariaSort}
      >
        <button
          type="button"
          className="sortable-th-btn"
          onClick={() => onSort(column)}
          title={`Sort by ${label}`}
        >
          <span>{label}</span>
          <span className="sort-indicator" aria-hidden="true">
            {isActive ? (activeDirection === 'asc' ? '▲' : '▼') : '↕'}
          </span>
        </button>
      </th>
    );
  };

  // Handle status change - show confirmation dialog
  // Supports both order_status and payment_status changes
  const handleStatusChange = (orderId, newStatus, currentStatus, statusType = 'order') => {
    // Find the order to get its details
    const order = orders.find(o => o.id === orderId);

    // Determine current status based on type
    const currentStatusValue = statusType === 'order'
      ? (order?.order_status || order?.status || 'pending')
      : (order?.payment_status || 'paid');

    if (newStatus === currentStatusValue) return;

    setPendingStatusChange({
      orderId,
      newStatus,
      currentStatus: currentStatusValue,
      order,
      statusType // 'order' or 'payment'
    });
    setSendEmailNotification(true); // Default to sending email
    setCustomMessage(''); // Clear custom message
    setSelectedCannedMessageForStatus(''); // Clear canned message selection
    setTrackingNumber(order?.tracking_number || ''); // Initialize with existing tracking number if any

    // Initialize with all items selected by default
    if (order && order.items) {
      setSelectedItems(order.items.map((_, index) => index));
    }

    setShowStatusConfirmModal(true);
  };

  // Confirm and execute status update
  const confirmStatusUpdate = async () => {
    if (!pendingStatusChange) return;

    const { orderId, newStatus, statusType } = pendingStatusChange;

    try {
      setUpdatingStatus(true);
      setShowStatusConfirmModal(false);
      const token = localStorage.getItem('token');

      // Build request body based on status type
      const requestBody = {
        sendEmail: sendEmailNotification,
        customMessage: customMessage.trim(),
        tracking_number: trackingNumber.trim() || null
      };

      if (statusType === 'order') {
        requestBody.order_status = newStatus;
      } else if (statusType === 'payment') {
        requestBody.payment_status = newStatus;
      }

      const response = await fetchNoCache(`${API_URL}/${orderId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update order status');
      }

      const result = await response.json();

      if (sendEmailNotification && result.emailSent) {
        showSuccess(`Order ${statusType === 'order' ? 'status' : 'payment status'} updated and notification email sent to customer`);
      } else {
        showSuccess(`Order ${statusType === 'order' ? 'status' : 'payment status'} updated successfully`);
      }

      // Update local state immediately so the UI reflects the change
      setOrders((prevOrders) => prevOrders.map((order) => {
        if (order.id === orderId) {
          const updated = {
            ...order,
            tracking_number: trackingNumber.trim() || null,
            notes_history: result.notes_history
              ? JSON.stringify(result.notes_history)
              : order.notes_history,
          };
          if (statusType === 'order') {
            updated.order_status = newStatus;
            updated.status = newStatus; // Legacy compatibility
          } else {
            updated.payment_status = newStatus;
          }
          return updated;
        }
        return order;
      }));

      // Update selected order if it's the one being updated
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder((prev) => {
          const updated = {
            ...prev,
            tracking_number: trackingNumber.trim() || null,
            notes_history: result.notes_history
              ? JSON.stringify(result.notes_history)
              : prev.notes_history,
          };
          if (statusType === 'order') {
            updated.order_status = newStatus;
            updated.status = newStatus; // Legacy compatibility
          } else {
            updated.payment_status = newStatus;
          }
          return updated;
        });
      }

      await fetchOrders(orderId, { silent: true });
    } catch (err) {
      console.error('Update status error:', err);
      showError(err.message);
    } finally {
      setUpdatingStatus(false);
      setPendingStatusChange(null);
    }
  };

  // Submit inquiry reply
  const submitInquiryReply = async () => {
    if (!inquiryReply.trim()) {
      showError('Please enter a reply message');
      return;
    }

    try {
      setSubmittingReply(true);
      const token = localStorage.getItem('token');
      const adminName = currentUser?.username || 'Cranford Publications Team';

      const response = await fetch(`${API_URL}/${selectedOrder.id}/inquiry/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reply: inquiryReply.trim(),
          adminName: adminName
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send reply');
      }

      const data = await response.json();

      showSuccess('Reply sent successfully! Customer has been notified via email.');

      // Update the selected order with new inquiry thread and history
      setSelectedOrder({
        ...selectedOrder,
        inquiry_thread: JSON.stringify(data.inquiryThread),
        notes_history: data.notes_history
          ? JSON.stringify(data.notes_history)
          : selectedOrder.notes_history,
      });

      // Update orders list
      setOrders(orders.map(order =>
        order.id === selectedOrder.id
          ? {
            ...order,
            inquiry_thread: JSON.stringify(data.inquiryThread),
            notes_history: data.notes_history
              ? JSON.stringify(data.notes_history)
              : order.notes_history,
          }
          : order
      ));

      setCollapsedSections((prev) => ({
        ...prev,
        [getAdminSectionKey(selectedOrder.id, ADMIN_SECTION_IDS.CONVERSATION)]: false,
      }));

      // Clear reply input
      setInquiryReply('');

    } catch (err) {
      console.error('Error sending reply:', err);
      showError(err.message || 'Failed to send reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  // Cancel status update
  const cancelStatusUpdate = () => {
    setShowStatusConfirmModal(false);
    setPendingStatusChange(null);
    setSendEmailNotification(true);
    setCustomMessage('');
    setSelectedCannedMessageForStatus('');
    setSelectedItems([]);
    setTrackingNumber('');
  };

  // Handle delete order
  const handleDeleteOrder = (order) => {
    setOrderToDelete(order);
    setShowDeleteConfirmModal(true);
  };

  // Confirm delete order
  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/${orderToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete order');
      }

      // Remove order from state
      setOrders(orders.filter(order => order.id !== orderToDelete.id));
      setFilteredOrders(filteredOrders.filter(order => order.id !== orderToDelete.id));
      setSelectedOrderIds((prev) => prev.filter((id) => id !== orderToDelete.id));

      showSuccess(`Order ${orderToDelete.order_number} deleted successfully`);

      // Close modals
      setShowDeleteConfirmModal(false);
      setShowDetailsModal(false);
      setOrderToDelete(null);
    } catch (err) {
      console.error('Delete order error:', err);
      showError(err.message);
    }
  };

  // Cancel delete order
  const cancelDeleteOrder = () => {
    setShowDeleteConfirmModal(false);
    setOrderToDelete(null);
  };

  // Get status badge color
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'status-badge status-pending';
      case 'processing':
        return 'status-badge status-processing';
      case 'shipped':
        return 'status-badge status-shipped';
      case 'delivered':
        return 'status-badge status-delivered';
      case 'completed':
        return 'status-badge status-delivered'; // Use same style as delivered
      case 'cancelled':
        return 'status-badge status-cancelled';
      case 'inquiry':
        return 'status-badge status-questions';
      case 'questions':
        return 'status-badge status-questions';
      default:
        return 'status-badge';
    }
  };

  const getPaymentStatusBadgeClass = (status) => {
    switch (status) {
      case 'paid':
        return 'status-badge status-paid';
      case 'pending_payment':
        return 'status-badge status-pending';
      case 'refunded':
        return 'status-badge status-refunded';
      case 'partially_refunded':
        return 'status-badge status-partially-refunded';
      default:
        return 'status-badge status-paid';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'processing':
        return '⚙️';
      case 'shipped':
        return '📦';
      case 'delivered':
        return '✅';
      case 'cancelled':
        return '❌';
      case 'questions':
        return '❓';
      case 'refunded':
        return '💰';
      case 'partially_refunded':
        return '💵';
      default:
        return '📋';
    }
  };

  // Calculate status counts (using order_status for fulfillment, payment_status for refunds)
  const getStatusCounts = () => {
    return {
      all: orders.length,
      pending: orders.filter(o => (o.order_status || o.status) === 'pending').length,
      processing: orders.filter(o => (o.order_status || o.status) === 'processing').length,
      shipped: orders.filter(o => (o.order_status || o.status) === 'shipped').length,
      delivered: orders.filter(o => (o.order_status || o.status) === 'delivered').length,
      cancelled: orders.filter(o => (o.order_status || o.status) === 'cancelled').length,
      questions: orders.filter(o => (o.order_status || o.status) === 'questions').length,
      refunded: orders.filter(o => {
        const paymentStatus = o.payment_status;
        return paymentStatus === 'refunded' || paymentStatus === 'partially_refunded';
      }).length
    };
  };

  const enrichOrderItems = async (order) => {
    if (!order?.items) return order.items || [];

    return Promise.all(order.items.map(async (item) => {
      if (!item.isBundle && !item.bundleProducts && item.id) {
        try {
          const response = await fetch(`/api/products/${item.id}`);
          if (response.ok) {
            const product = await response.json();
            if (product.is_bundle === 1 || product.is_bundle === true) {
              let bundleProducts = [];
              if (product.bundle_products) {
                const productIds = product.bundle_products.split(',').filter((id) => id.trim());
                try {
                  const bundleProductPromises = productIds.map((id) =>
                    fetch(`/api/products/${id.trim()}`)
                      .then((res) => (res.ok ? res.json() : null))
                      .catch(() => null)
                  );
                  bundleProducts = (await Promise.all(bundleProductPromises))
                    .filter((p) => p !== null)
                    .map((p) => ({
                      id: p.id,
                      name: p.name,
                      title: p.name,
                      price_cents: p.price_cents || p.price || 0,
                      price: p.price_cents || p.price || 0,
                    }));
                } catch (err) {
                  console.warn('Could not fetch bundle product details:', err);
                  bundleProducts = productIds.map((id) => ({ id: id.trim() }));
                }
              }

              return {
                ...item,
                isBundle: true,
                is_bundle: true,
                bundleProducts,
                bundleDiscount: product.bundle_discount || 0,
                originalPrice: item.originalPrice || bundleProducts.reduce(
                  (sum, p) => sum + (p.price_cents || p.price || 0),
                  0
                ),
              };
            }
          }
        } catch (err) {
          console.warn('Could not fetch product data for item:', item.id, err);
        }
      }
      return item;
    }));
  };

  // View order details
  const handleViewDetails = async (order) => {
    let orderToShow = order;

    // Fetch the latest order data so history/inquiries are up to date
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_URL, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const freshOrder = (data.orders || []).find((o) => o.id === order.id);
        if (freshOrder) {
          orderToShow = freshOrder;
          setOrders((prev) => prev.map((o) => (o.id === freshOrder.id ? freshOrder : o)));
          setFilteredOrders((prev) => prev.map((o) => (o.id === freshOrder.id ? freshOrder : o)));
        }
      }
    } catch (err) {
      console.warn('Could not refresh order before opening details:', err);
    }

    setSelectedOrder(orderToShow);
    if (orderToShow?.items) {
      setSelectedItems(orderToShow.items.map((_, index) => index));
      const enrichedItems = await enrichOrderItems(orderToShow);
      setSelectedOrder({ ...orderToShow, items: enrichedItems });
    }

    if (getAdminInquiryThread(orderToShow).length > 0) {
      setCollapsedSections((prev) => ({
        ...prev,
        [getAdminSectionKey(orderToShow.id, ADMIN_SECTION_IDS.CONVERSATION)]: false,
      }));
    }

    setShowDetailsModal(true);

    if (orderToShow.payment_intent_id || orderToShow.paypal_order_id) {
      fetchPaymentBreakdownForOrder(orderToShow);
    } else {
      setPaymentBreakdown(null);
    }
  };

  // Close details modal
  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedItems([]);
    // Keep selectedOrder so header buttons remain available
    // setSelectedOrder(null);
  };

  // Toggle item selection
  const toggleItemSelection = (itemIndex) => {
    setSelectedItems(prev => {
      if (prev.includes(itemIndex)) {
        return prev.filter(i => i !== itemIndex);
      } else {
        return [...prev, itemIndex];
      }
    });
  };

  // Select all items
  const selectAllItems = () => {
    if (selectedOrder && selectedOrder.items) {
      setSelectedItems(selectedOrder.items.map((_, index) => index));
    }
  };

  // Deselect all items
  const deselectAllItems = () => {
    setSelectedItems([]);
  };

  // Format currency
  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Build CSV content for a list of orders
  const buildOrdersCsv = (ordersToExport) => {
    const headers = [
      'Order Number',
      'Date',
      'Status',
      'Customer Name',
      'Customer Email',
      'Customer Phone',
      'Shipping Address',
      'Item Count',
      'Items Detail',
      'Subtotal',
      'Shipping',
      'Tax',
      'Total',
      'Payment Method',
      'Notes/Messages'
    ];

    const rows = ordersToExport.map(order => {
      const items = order.items || [];
      const itemsDetail = items.map(item =>
        `${item.name || `Product #${item.id}`} (Qty: ${item.quantity}, Price: ${formatCurrency(item.unitPrice ?? item.price_cents ?? item.price ?? 0)})`
      ).join('; ');

      let notesText = '';
      try {
        const notes = typeof order.notes_history === 'string'
          ? JSON.parse(order.notes_history)
          : order.notes_history || [];
        notesText = notes.map(note =>
          `[${formatDateAtlantic(note.timestamp)}] ${note.status}: ${note.message}${note.sentViaEmail ? ' (Emailed)' : ''}`
        ).join(' | ');
      } catch (e) {
        notesText = '';
      }

      const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

      return [
        escapeCsv(order.order_number),
        escapeCsv(formatOrderDateForCsv(order.created_at_full || order.created_at)),
        escapeCsv(order.order_status || order.status || ''),
        escapeCsv(order.customer_name),
        escapeCsv(order.customer_email),
        escapeCsv(order.customer_phone || ''),
        escapeCsv((order.shipping_address || '').replace(/\n/g, ', ')),
        items.length,
        escapeCsv(itemsDetail),
        escapeCsv(formatCurrency(order.subtotal)),
        escapeCsv(formatCurrency(order.shipping || 0)),
        escapeCsv(formatCurrency(order.tax || 0)),
        escapeCsv(formatCurrency(order.total)),
        escapeCsv(order.payment_method || 'Credit Card'),
        escapeCsv(notesText)
      ].join(',');
    });

    return [headers.join(','), ...rows].join('\n');
  };

  const downloadTextFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getOrdersForExport = (scope) => {
    if (scope === 'selected') {
      const selected = getSelectedOrders();
      if (selected.length === 0) {
        showError('Select one or more orders to export, or choose Export all.');
        return null;
      }
      return selected;
    }
    if (filteredOrders.length === 0) {
      showError('No orders to export');
      return null;
    }
    return filteredOrders;
  };

  // Export orders to CSV (selected or all filtered)
  const exportToCSV = (scope = 'all') => {
    const ordersToExport = getOrdersForExport(scope);
    if (!ordersToExport) return;

    const csvContent = buildOrdersCsv(ordersToExport);
    const scopeLabel = scope === 'selected' ? 'selected' : 'all';
    const filename = `orders_export_${scopeLabel}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadTextFile(csvContent, filename, 'text/csv;charset=utf-8;');
    showSuccess(`Exported ${ordersToExport.length} order${ordersToExport.length !== 1 ? 's' : ''} to ${filename}`);
  };

  const formatCsvDecimal = (cents) => {
    if (cents == null || Number.isNaN(Number(cents))) return '';
    return (Number(cents) / 100).toFixed(2);
  };

  const buildFeesExportCsv = (rows, totals) => {
    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const headers = [
      'Order Number',
      'Date',
      'Customer Name',
      'Customer Email',
      'Payment Provider',
      'Environment',
      'Payment Method',
      'Payment Status',
      'Order Status',
      'Transaction ID',
      'Gross Amount (CAD)',
      'Processing Fee (CAD)',
      'Refunds (CAD)',
      'Net Payout (CAD)',
      'Order Total (CAD)',
      'Fee Source',
      'Notes',
    ];

    const dataRows = (rows || []).map((row) => [
      escapeCsv(row.orderNumber),
      escapeCsv(formatOrderDateForCsv(row.date)),
      escapeCsv(row.customerName),
      escapeCsv(row.customerEmail),
      escapeCsv(row.provider),
      escapeCsv(row.environment),
      escapeCsv(row.paymentMethod),
      escapeCsv(row.paymentStatus),
      escapeCsv(row.orderStatus),
      escapeCsv(row.transactionId),
      escapeCsv(formatCsvDecimal(row.grossCents)),
      escapeCsv(formatCsvDecimal(row.processingFeeCents)),
      escapeCsv(formatCsvDecimal(row.refundedCents)),
      escapeCsv(formatCsvDecimal(row.netPayoutCents)),
      escapeCsv(formatCsvDecimal(row.orderTotalCents)),
      escapeCsv(row.feeSource),
      escapeCsv(row.notes),
    ].join(','));

    const summaryRows = [];
    if (totals) {
      summaryRows.push('');
      summaryRows.push([
        escapeCsv('TOTALS (online Stripe/PayPal only)'),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        escapeCsv(`${totals.onlinePaymentCount || 0} of ${totals.orderCount || 0} orders`),
        escapeCsv(formatCsvDecimal(totals.grossCents)),
        escapeCsv(formatCsvDecimal(totals.processingFeeCents)),
        escapeCsv(formatCsvDecimal(totals.refundedCents)),
        escapeCsv(formatCsvDecimal(totals.netPayoutCents)),
        '',
        '',
        escapeCsv('Use for tax / accounting; fees from Stripe & PayPal APIs'),
      ].join(','));
    }

    return [headers.join(','), ...dataRows, ...summaryRows].join('\n');
  };

  const exportProcessingFees = async (scope = 'all') => {
    const ordersToExport = getOrdersForExport(scope);
    if (!ordersToExport) return;

    setExportingFees(true);
    try {
      const response = await fetch('/api/payment/fees-export', {
        method: 'POST',
        headers: getAdminPaymentHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ orderIds: ordersToExport.map((order) => order.id) }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to export processing fees');
      }

      const scopeLabel = scope === 'selected' ? 'selected' : 'all';
      const filename = `payment_fees_tax_${scopeLabel}_${new Date().toISOString().split('T')[0]}.csv`;
      const csvContent = buildFeesExportCsv(data.rows, data.totals);
      downloadTextFile(csvContent, filename, 'text/csv;charset=utf-8;');

      const feeTotal = formatCurrency(data.totals?.processingFeeCents || 0);
      const onlineCount = data.totals?.onlinePaymentCount || 0;
      showSuccess(
        `Exported fees for ${ordersToExport.length} order${ordersToExport.length !== 1 ? 's' : ''} `
        + `(${onlineCount} with Stripe/PayPal data). Total processing fees: ${feeTotal}`
      );
    } catch (error) {
      console.error('Failed to export processing fees:', error);
      showError(error.message || 'Failed to export processing fees');
    } finally {
      setExportingFees(false);
    }
  };

  // Generate Invoice HTML
  const generateInvoiceHTML = (order) => {
    const items = order.items || [];
    // Filter out packing items (isPartOfBundle) - they're already represented by the bundle item
    const invoiceItems = items.filter(item => !item.isPartOfBundle || item.isBundle);

    const itemsRows = invoiceItems.map(item => {
      const isBundle = item.isBundle || item.is_bundle || false;
      const bundleProducts = item.bundleProducts || [];
      const bundleDiscount = item.bundleDiscount || 0;
      const originalPrice = item.originalPrice || 0;
      const itemPrice = item.unitPrice || item.price_cents || item.price || 0;

      // Check if this bundle has expanded products (isPartOfBundle items)
      const expandedProducts = items.filter(i => i.isPartOfBundle && i.bundleParentId === item.id);

      // Use expanded products if available, otherwise use bundleProducts
      const productsToDisplay = expandedProducts.length > 0
        ? expandedProducts.map(ep => ({ name: ep.name, id: ep.id }))
        : bundleProducts;

      // Calculate bundle savings if applicable
      let bundleSavings = 0;
      if (isBundle && bundleDiscount > 0 && originalPrice > 0) {
        bundleSavings = (originalPrice - itemPrice) * item.quantity;
      }

      // Format bundle products list for display
      let bundleProductsList = '';
      if (isBundle && productsToDisplay.length > 0) {
        bundleProductsList = productsToDisplay.map(bp => {
          const productName = bp.name || bp.title || `Product #${bp.id || bp.productId || ''}`;
          return productName;
        }).join(', ');
      }

      return `
      <tr>
        <td style="padding: 12px 8px; border-bottom: 1px solid #e5e7eb;">
          ${item.name || `Product #${item.id}`}
          ${(item.selectedSize || item.selectedColor) ? `<br><span style="color: #666; font-size: 11px;">${item.selectedSize ? `Size: <strong>${item.selectedSize}</strong> ` : ''}${item.selectedColor ? `Color: <strong>${item.selectedColor}</strong>` : ''}</span>` : ''}
          ${isBundle ? '<span style="background: linear-gradient(135deg, #4a90e2, #5b9ceb); color: white; padding: 2px 8px; border-radius: 12px; font-size: 10px; font-weight: 600; margin-left: 8px; display: inline-block;">📦 BUNDLE</span>' : ''}
          ${isBundle && productsToDisplay.length > 0 ? `<br><span style="color: #6b7280; font-size: 11px; margin-top: 4px; display: block;">Includes ${productsToDisplay.length} product${productsToDisplay.length !== 1 ? 's' : ''}: ${bundleProductsList}</span>` : ''}
          ${isBundle && bundleSavings > 0 ? `<br><span style="color: #10b981; font-size: 11px; font-weight: 600; margin-top: 4px; display: block;">🎉 Save $${(bundleSavings / 100).toFixed(2)} (${bundleDiscount}% off)</span>` : ''}
          ${item.isWholesalePrice && item.wholesaleTier ? `<br><span style="color: #0369a1; font-size: 11px; font-weight: 600;">🏢 Wholesale (${item.wholesaleTier})</span>` : ''}
        </td>
        <td style="padding: 12px 8px; text-align: center; border-bottom: 1px solid #e5e7eb;">${item.quantity}</td>
        <td style="padding: 12px 8px; text-align: right; border-bottom: 1px solid #e5e7eb;">${formatCurrency(itemPrice)}</td>
        <td style="padding: 12px 8px; text-align: right; border-bottom: 1px solid #e5e7eb; font-weight: 600;">${formatCurrency(itemPrice * item.quantity)}</td>
      </tr>
    `;
    }).join('');

    const refunds = parseOrderRefunds(order);
    const totalRefunded = getTotalRefunded(order);
    const netTotal = Math.max(0, order.total - totalRefunded);
    const paymentStatus = order.payment_status || 'paid';
    const paymentStatusLabel = paymentStatus === 'paid'
      ? ''
      : ` (${paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1).replace(/_/g, ' ')})`;

    const refundRows = refunds.map((refund, refundIndex) => {
      const amount = refund.refundAmount || 0;
      let explanation = refund.explanation || '';
      if (!explanation && refund.message) {
        const explanationMatch = refund.message.match(/Explanation:\s*(.+)/i);
        if (explanationMatch) {
          explanation = explanationMatch[1].trim();
        } else {
          explanation = refund.message.replace(/^Refund processed:.*?CAD\s*/i, '').trim();
        }
      }
      const refundDate = refund.timestamp ? formatDateOnlyAtlantic(refund.timestamp) : '';

      return `
      <tr style="background: #fef2f2;">
        <td style="padding: 12px 8px; border-bottom: 1px solid #e5e7eb; color: #dc2626;">
          💰 Refund${refundDate ? ` (${refundDate})` : ''}
          ${explanation ? `<br><span style="color: #666; font-size: 11px;">${explanation}</span>` : ''}
        </td>
        <td style="padding: 12px 8px; text-align: center; border-bottom: 1px solid #e5e7eb;">—</td>
        <td style="padding: 12px 8px; text-align: right; border-bottom: 1px solid #e5e7eb;">—</td>
        <td style="padding: 12px 8px; text-align: right; border-bottom: 1px solid #e5e7eb; font-weight: 600; color: #dc2626;">-${formatCurrency(amount)}</td>
      </tr>
    `;
    }).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Invoice ${order.order_number}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 30px; color: #333; }
          .invoice-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; border-bottom: 3px solid #22c55e; padding-bottom: 15px; }
          .company-info { flex: 1; }
          .company-name-line { display: flex; align-items: center; gap: 10px; margin: 0 0 5px 0; }
          .company-name { font-size: 24px; font-weight: bold; color: #22c55e; margin: 0; }
          .company-details { font-size: 11px; color: #666; line-height: 1.4; }
          .invoice-title-block { text-align: right; }
          .invoice-title { font-size: 28px; margin: 0; color: #22c55e; font-weight: bold; }
          .invoice-info { display: flex; justify-content: space-between; margin: 15px 0; gap: 20px; }
          .info-block { flex: 1; }
          .info-block h3 { margin: 0 0 8px 0; font-size: 13px; color: #666; text-transform: uppercase; }
          .info-block p { margin: 3px 0; font-size: 13px; line-height: 1.5; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th { background: #22c55e; color: white; padding: 10px 8px; text-align: left; font-size: 13px; }
          td { padding: 10px 8px; font-size: 13px; }
          .totals { margin-left: auto; width: 280px; margin-top: 10px; }
          .totals-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; }
          .totals-row.total { border-top: 2px solid #22c55e; font-weight: bold; font-size: 16px; margin-top: 8px; padding-top: 8px; }
          .footer { margin-top: 30px; background: #f9f9f9; padding: 15px 20px; border-top: 3px solid #22c55e; }
          .footer-content { display: flex; justify-content: space-between; gap: 30px; }
          .footer-section { flex: 1; font-size: 11px; line-height: 1.6; }
          .footer-section h4 { margin: 0 0 5px 0; font-size: 12px; color: #22c55e; }
          @media print { body { margin: 15px; } }
        </style>
      </head>
      <body>
        <div class="invoice-header">
          <div class="company-info">
            <div class="company-name-line">
              <span style="font-size: 32px;">🎻</span>
              <h1 class="company-name">CRANFORD PUBLICATIONS</h1>
            </div>
            <div class="company-details">
              <strong>Paul Cranford</strong><br>
              Cape Breton Island, Nova Scotia, Canada<br>
              📧 admin@cranfordpub.ca | 📞 1-902-929-2391 <br>
              HST Registration: 89023 4395 RT0001
            </div>
          </div>
          <div class="invoice-title-block">
            <h2 class="invoice-title">INVOICE</h2>
            <p style="margin: 5px 0; font-size: 12px; color: #666;">${SITE_TAGLINE_SHORT}</p>
          </div>
        </div>
        
        <div class="invoice-info">
          <div class="info-block">
            <h3>Bill To:</h3>
            <p><strong>${order.customer_name}</strong></p>
            <p>${order.customer_email}</p>
            <p>${order.customer_phone || ''}</p>
          </div>
          <div class="info-block">
            <h3>Ship To:</h3>
            <p style="white-space: pre-line;">${order.shipping_address}</p>
          </div>
          <div class="info-block">
            <h3>Invoice Details:</h3>
            <p><strong>Invoice #:</strong> ${order.order_number}</p>
            <p><strong>Date:</strong> ${formatDateWithTime(order)}</p>
            <p><strong>Status:</strong> ${order.status.toUpperCase()}</p>
            <p><strong>Payment:</strong> ${order.payment_method || 'Credit Card'}${paymentStatusLabel}</p>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th style="text-align: center; width: 80px;">Qty</th>
              <th style="text-align: right; width: 100px;">Unit Price</th>
              <th style="text-align: right; width: 120px;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${itemsRows}
            ${refundRows}
          </tbody>
        </table>
        
        <div class="totals">
          <div class="totals-row">
            <span>Subtotal:</span>
            <span>${formatCurrency(order.subtotal)}</span>
          </div>
          <div class="totals-row">
            <span>Shipping${(() => {
              let sd = null;
              try { sd = order.shipping_details_json ? JSON.parse(order.shipping_details_json) : null; } catch(e) {}
              return sd?.bandLabel ? ` <span style="font-size:11px;color:#666;">(${sd.bandLabel})</span>` : '';
            })()}:</span>
            <span>${order.shipping > 0 ? formatCurrency(order.shipping) : 'FREE'}</span>
          </div>
          <div class="totals-row">
            <span>Tax:</span>
            <span>${formatCurrency(order.tax || 0)}</span>
          </div>
          <div class="totals-row total">
            <span>TOTAL:</span>
            <span>${formatCurrency(order.total)}</span>
          </div>
          ${totalRefunded > 0 ? `
          <div class="totals-row">
            <span>Refunds:</span>
            <span style="color: #dc2626;">-${formatCurrency(totalRefunded)}</span>
          </div>
          <div class="totals-row total">
            <span>NET AMOUNT PAID:</span>
            <span>${formatCurrency(netTotal)}</span>
          </div>
          ` : ''}
        </div>
        
        <div class="footer">
          <div class="footer-content">
            <div class="footer-section">
              <h4>🎻 Cranford Publications</h4>
              <p>
                <strong>Paul Cranford</strong><br>
                44549 Cabot Trail, RR#1<br>
                Englishtown, Cape Breton Island<br>
                Nova Scotia, Canada B0C 1H0
              </p>
            </div>
            <div class="footer-section">
              <h4>Contact Information</h4>
              <p>
                📧 <a href="mailto:admin@cranfordpub.ca">admin@cranfordpub.ca</a><br>
                📞 <a href="tel:19029292391">1-902-929-2391</a> <br>
                🌐 <a href="http://www.cranfordpub.ca">www.cranfordpub.ca</a>
              </p>
            </div>
            <div class="footer-section">
              <h4>Tax Information</h4>
              <p>
                <strong>HST Registration:</strong><br>
                89023 4395 RT0001<br>
                <br>
                <em>Thank you for supporting<br>traditional Celtic music!</em>
              </p>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Generate Shipping Label HTML
  const generateShippingLabelHTML = (order) => {
    const items = order.items || [];

    // Collect all items to pack
    const itemsToDisplay = [];
    const processedBundleProductIds = new Set();

    // First, add all packing items (isPartOfBundle) and standalone items
    items.forEach(item => {
      const isBundle = item.isBundle || item.is_bundle || false;
      const isPartOfBundle = item.isPartOfBundle || false;
      const hasBundleProducts = item.bundleProducts && Array.isArray(item.bundleProducts) && item.bundleProducts.length > 0;

      if (isPartOfBundle) {
        // This is an expanded packing item
        itemsToDisplay.push({
          name: item.name || `Product #${item.id}`,
          quantity: item.quantity || 1,
          isPartOfBundle: true,
          bundleParentName: item.bundleParentName || 'Bundle',
          id: item.id,
          selectedSize: item.selectedSize,
          selectedColor: item.selectedColor
        });
        if (item.id) processedBundleProductIds.add(item.id);
      } else if (!isBundle && !hasBundleProducts) {
        // Standalone item (not a bundle)
        itemsToDisplay.push({
          name: item.name || `Product #${item.id}`,
          quantity: item.quantity || 1,
          isPartOfBundle: false,
          id: item.id,
          selectedSize: item.selectedSize,
          selectedColor: item.selectedColor
        });
      }
    });

    // Then, expand any bundles that haven't been expanded yet
    items.forEach(item => {
      const isBundle = item.isBundle || item.is_bundle || false;
      const hasBundleProducts = item.bundleProducts && Array.isArray(item.bundleProducts) && item.bundleProducts.length > 0;

      if ((isBundle || hasBundleProducts) && !item.isPartOfBundle) {
        // This is a bundle - expand it to show all products
        if (item.bundleProducts && Array.isArray(item.bundleProducts) && item.bundleProducts.length > 0) {
          item.bundleProducts.forEach(bp => {
            const bpId = bp.id || bp.productId;
            // Only add if not already in the list
            if (!processedBundleProductIds.has(bpId)) {
              const productName = bp.name || bp.title || `Product #${bpId || ''}`;
              itemsToDisplay.push({
                name: productName,
                quantity: item.quantity || 1,
                isPartOfBundle: true,
                bundleParentName: item.name || 'Bundle',
                id: bpId
              });
              if (bpId) processedBundleProductIds.add(bpId);
            }
          });
        }
      }
    });

    // Count total physical items to pack
    const totalPhysicalItems = itemsToDisplay.reduce((sum, item) => sum + (item.quantity || 1), 0);

    // Build items list for display
    let itemsListHTML = '';
    if (itemsToDisplay.length > 0) {
      itemsListHTML = itemsToDisplay.map(item => {
        const bundleNote = item.isPartOfBundle ? ` (from: ${item.bundleParentName || 'Bundle'})` : '';
        const variantNote = (item.selectedSize || item.selectedColor)
          ? ` [${item.selectedSize ? `Size: ${item.selectedSize}` : ''}${item.selectedSize && item.selectedColor ? ', ' : ''}${item.selectedColor ? `Color: ${item.selectedColor}` : ''}]`
          : '';
        return `${item.name || `Product #${item.id}`} × ${item.quantity || 1}${variantNote}${bundleNote}`;
      }).join('<br>');
    }

    // Format shipping address more compactly
    const formatShippingAddress = (address) => {
      if (!address) return '';

      // Split by line breaks and clean up - remove empty lines and trim
      const lines = address
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => line.length > 0);

      if (lines.length === 0) return '';

      // If address is already compact (3-4 lines), return as is
      if (lines.length <= 4) {
        return lines.join('<br>');
      }

      // For longer addresses, try to combine related lines
      const formattedLines = [];
      let i = 0;

      while (i < lines.length) {
        const currentLine = lines[i];

        // Skip if this is the name (already shown separately)
        if (i === 0 && currentLine === order.customer_name) {
          i++;
          continue;
        }

        // Try to combine with next line if both are short and related
        if (i < lines.length - 1) {
          const nextLine = lines[i + 1];

          // Combine if both lines are short (likely street address parts)
          if (currentLine.length < 40 && nextLine.length < 40 &&
            !nextLine.match(/^[A-Z]{2}\s+\d/)) { // Don't combine if next is state + zip
            formattedLines.push(`${currentLine}, ${nextLine}`);
            i += 2;
            continue;
          }

          // Combine city, state/province, postal code if on separate lines
          if (i < lines.length - 2) {
            const thirdLine = lines[i + 2];
            // Check if we have city, state, zip pattern
            if (currentLine.match(/^[A-Za-z\s,]+$/) &&
              nextLine.match(/^[A-Za-z\s]+$/) &&
              thirdLine.match(/^[A-Z0-9\s-]{3,12}$/)) {
              formattedLines.push(`${currentLine}, ${nextLine} ${thirdLine}`);
              i += 3;
              continue;
            }
          }
        }

        formattedLines.push(currentLine);
        i++;
      }

      // Limit to max 5 lines for compactness
      return formattedLines.slice(0, 5).join('<br>');
    };

    const formattedShippingAddress = formatShippingAddress(order.shipping_address);

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Shipping Label ${order.order_number}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 15px; }
          .label-container { border: 3px solid #000; padding: 15px; max-width: 600px; margin: 0 auto; }
          .from-section { margin-bottom: 20px; border-bottom: 2px dashed #000; padding-bottom: 15px; }
          .to-section { margin-bottom: 15px; }
          .section-title { font-size: 11px; font-weight: bold; text-transform: uppercase; margin-bottom: 8px; color: #666; }
          .company-header { display: flex; align-items: center; gap: 8px; margin-bottom: 5px; }
          .address { font-size: 16px; line-height: 1.4; }
          .address-compact { font-size: 15px; line-height: 1.3; }
          .order-info { background: #f5f5f5; padding: 12px; margin: 15px 0; border: 1px solid #ddd; }
          .order-info-row { display: flex; justify-content: space-between; margin: 4px 0; font-size: 13px; }
          .items-list { margin-top: 8px; font-size: 12px; line-height: 1.6; }
          .barcode-area { text-align: center; margin: 15px 0; padding: 15px; border: 2px solid #000; background: white; }
          .barcode-text { font-family: 'Courier New', monospace; font-size: 22px; font-weight: bold; letter-spacing: 3px; }
          .footer-info { background: #f9f9f9; padding: 10px; margin-top: 15px; border-top: 2px solid #22c55e; font-size: 10px; line-height: 1.5; }
          @media print { body { margin: 0; padding: 10px; } .label-container { max-width: 100%; } }
        </style>
      </head>
      <body>
        <div class="label-container">
          <div class="from-section">
            <div class="section-title">FROM:</div>
            <div class="company-header">
              <span style="font-size: 24px;">🎻</span>
              <strong style="font-size: 18px;">CRANFORD PUBLICATIONS</strong>
            </div>
            <div class="address" style="font-size: 14px;">
              <strong>Paul Cranford</strong><br>
              44549 Cabot Trail, RR#1<br>
              Englishtown, Cape Breton Island<br>
              Nova Scotia, Canada B0C 1H0<br>
              📧 admin@cranfordpub.ca | 📞 1-902-929-2391<br>
              HST: 89023 4395 RT0001
            </div>
          </div>
          
          <div class="to-section">
            <div class="section-title">SHIP TO:</div>
            <div class="address address-compact">
              <strong>${order.customer_name}</strong><br>
              ${formattedShippingAddress}
              ${order.customer_phone ? `<br><strong>Phone:</strong> ${order.customer_phone}` : ''}
            </div>
          </div>
          
          <div class="order-info">
            <div class="order-info-row">
              <span><strong>Order #:</strong></span>
              <span>${order.order_number}</span>
            </div>
            <div class="order-info-row">
              <span><strong>Date:</strong></span>
              <span>${formatDateWithTime(order)}</span>
            </div>
            <div class="order-info-row">
              <span><strong>Items to Pack:</strong></span>
              <span>${totalPhysicalItems} item(s)</span>
            </div>
            ${itemsListHTML ? `
            <div class="items-list">
              <strong>Packing List:</strong><br>
              ${itemsListHTML}
            </div>
            ` : ''}
            ${(() => {
              let shippingDetails = null;
              try { shippingDetails = order.shipping_details_json ? JSON.parse(order.shipping_details_json) : null; } catch (e) {}
              const weightLine = shippingDetails?.totalWeightKg
                ? `${shippingDetails.totalWeightKg.toFixed(2)} kg`
                : 'TBD';
              const bandLine = shippingDetails?.bandLabel || '';
              const methodLine = shippingDetails?.method || '';
              return `
                <div class="order-info-row" style="margin-top: 8px;">
                  <span><strong>Weight:</strong></span>
                  <span>${weightLine}</span>
                </div>
                ${bandLine ? `
                <div class="order-info-row">
                  <span><strong>Shipping Band:</strong></span>
                  <span>${bandLine}</span>
                </div>
                ` : ''}
                ${methodLine ? `
                <div class="order-info-row">
                  <span><strong>Method:</strong></span>
                  <span style="text-transform: capitalize;">${methodLine}</span>
                </div>
                ` : ''}
              `;
            })()}
          </div>
          
          <div class="barcode-area">
            <div class="barcode-text">${order.order_number}</div>
            <div style="margin-top: 8px; font-size: 11px;">Tracking Number</div>
          </div>
          
          <div class="footer-info">
            🎻 <strong>${SITE_TAGLINE_SHORT}</strong><br>
            Handle with care - Contains traditional Celtic music materials<br>
            For questions: admin@cranfordpub.ca | 1-902-929-2391 | www.cranfordpub.ca
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const getBulkActionOrders = () => {
    const selected = getSelectedOrders();
    if (selected.length > 0) {
      return selected;
    }
    if (selectedOrder) {
      return [selectedOrder];
    }
    showError('Select one or more orders first');
    return null;
  };

  // Print Invoice(s)
  const handlePrintInvoice = async () => {
    const selected = getBulkActionOrders();
    if (!selected) return;

    const enrichedOrders = await Promise.all(selected.map(ensureOrderEnriched));
    const combinedHTML = enrichedOrders.length === 1
      ? generateInvoiceHTML(enrichedOrders[0])
      : combinePrintDocuments(enrichedOrders.map(generateInvoiceHTML));

    openPrintWindow(
      combinedHTML,
      enrichedOrders.length === 1
        ? 'Opening invoice for printing...'
        : `Opening ${enrichedOrders.length} invoices for printing...`
    );
  };

  // Save Invoice(s) as HTML
  const handleSaveInvoice = async () => {
    const selected = getBulkActionOrders();
    if (!selected) return;

    const enrichedOrders = await Promise.all(selected.map(ensureOrderEnriched));
    const combinedHTML = enrichedOrders.length === 1
      ? generateInvoiceHTML(enrichedOrders[0])
      : combinePrintDocuments(enrichedOrders.map(generateInvoiceHTML));
    const blob = new Blob([combinedHTML], { type: 'text/html' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const filename = enrichedOrders.length === 1
      ? `invoice_${enrichedOrders[0].order_number}.html`
      : `invoices_${enrichedOrders.length}_orders.html`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showSuccess(`Invoice${enrichedOrders.length === 1 ? '' : 's'} saved as ${filename}`);
  };

  // Email Invoice(s)
  const sendInvoiceEmail = async (order, { checkInstructions = '' } = {}) => {
    let invoiceHTML = generateInvoiceHTML(order);
    if (checkInstructions) {
      const block = `
        <div style="margin-top: 24px; padding: 16px; background: #fffbeb; border-left: 4px solid #f59e0b; border-radius: 6px;">
          <h3 style="margin: 0 0 8px 0; color: #92400e; font-size: 16px;">Payment instructions</h3>
          <p style="margin: 0; color: #78350f; line-height: 1.6; font-size: 14px;">${checkInstructions.replace(/\n/g, '<br>')}</p>
        </div>`;
      invoiceHTML = invoiceHTML.replace('</body>', `${block}</body>`);
    }

    const response = await fetch('/api/v1/email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: order.customer_email,
        from: ORDERS_EMAIL_FROM,
        replyTo: ORDERS_EMAIL_ADDRESS,
        subject: `Invoice for Order ${order.order_number}`,
        body: invoiceHTML,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to send invoice for ${order.order_number}`);
    }
  };

  const handleEmailMailOrderInvoice = async (order, options = {}) => {
    await sendMailOrderInvoiceEmail(order, options);
  };

  const handleMailOrderCreated = async (order) => {
    const testNote = order?.mail_order_mode === 'test' ? ' (test)' : '';
    showSuccess(`Mail order ${order.order_number} created${testNote}${order.payment_status === 'pending_payment' ? ' — payment pending' : ''}.`);
    await fetchOrders();
    await fetchCartInquiries();
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const handleMarkPaymentReceived = async (order) => {
    if (!order?.id) return;
    const confirmed = window.confirm(
      `Mark order ${order.order_number} as paid?\n\nUse this when check, cash, or mail payment has been received.`
    );
    if (!confirmed) return;

    try {
      setMarkingPaymentReceived(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/${order.id}/mark-paid`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sendEmail: false }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to mark as paid');

      showSuccess(`Order ${order.order_number} marked as paid.`);
      await fetchOrders();
      if (selectedOrder?.id === order.id) {
        setSelectedOrder({ ...selectedOrder, payment_status: 'paid' });
      }
    } catch (error) {
      showError(error.message || 'Failed to mark payment received');
    } finally {
      setMarkingPaymentReceived(false);
    }
  };

  const handleEmailInvoice = async () => {
    const selected = getBulkActionOrders();
    if (!selected) return;

    const confirmSend = window.confirm(
      selected.length === 1
        ? `Send invoice for order ${selected[0].order_number} to ${selected[0].customer_email}?`
        : `Send invoices for ${selected.length} orders (one email per customer)?`
    );
    if (!confirmSend) return;

    try {
      const enrichedOrders = await Promise.all(selected.map(ensureOrderEnriched));
      let sentCount = 0;

      for (const order of enrichedOrders) {
        await sendInvoiceEmail(order);
        sentCount += 1;
      }

      showSuccess(
        sentCount === 1
          ? `Invoice emailed to ${enrichedOrders[0].customer_email}`
          : `${sentCount} invoices emailed successfully`
      );
    } catch (error) {
      showError('Failed to email invoice. Please try again.');
      console.error('Email invoice error:', error);
    }
  };

  // Print Shipping Label(s)
  const handlePrintShippingLabel = async () => {
    const selected = getBulkActionOrders();
    if (!selected) return;

    const enrichedOrders = await Promise.all(selected.map(ensureOrderEnriched));
    const combinedHTML = enrichedOrders.length === 1
      ? generateShippingLabelHTML(enrichedOrders[0])
      : combinePrintDocuments(enrichedOrders.map(generateShippingLabelHTML));

    openPrintWindow(
      combinedHTML,
      enrichedOrders.length === 1
        ? 'Opening shipping label for printing...'
        : `Opening ${enrichedOrders.length} shipping labels for printing...`
    );
  };

  // Save Shipping Label(s)
  const handleSaveShippingLabel = async () => {
    const selected = getBulkActionOrders();
    if (!selected) return;

    const enrichedOrders = await Promise.all(selected.map(ensureOrderEnriched));
    const combinedHTML = enrichedOrders.length === 1
      ? generateShippingLabelHTML(enrichedOrders[0])
      : combinePrintDocuments(enrichedOrders.map(generateShippingLabelHTML));
    const blob = new Blob([combinedHTML], { type: 'text/html' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const filename = enrichedOrders.length === 1
      ? `shipping_label_${enrichedOrders[0].order_number}.html`
      : `shipping_labels_${enrichedOrders.length}_orders.html`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showSuccess(`Shipping label${enrichedOrders.length === 1 ? '' : 's'} saved as ${filename}`);
  };

  // Handle canned message selection for email follow-up modal
  const handleCannedMessageSelect = (cannedId) => {
    if (!selectedOrder || !cannedId) {
      setSelectedCannedMessage('');
      return;
    }

    const canned = cannedMessages.find(m => m.id === cannedId);
    if (!canned) return;

    // Replace placeholders with actual values
    let subject = canned.subject;
    let message = canned.message;

    subject = subject.replace(/{orderNumber}/g, selectedOrder.order_number);
    message = message.replace(/{customerName}/g, selectedOrder.customer_name);
    message = message.replace(/{orderNumber}/g, selectedOrder.order_number);

    setEmailSubject(subject);
    setEmailMessage(message);
    setSelectedCannedMessage(cannedId);
  };

  // Handle canned message selection for status update modal
  const handleCannedMessageSelectForStatus = (cannedId) => {
    if (!pendingStatusChange || !cannedId) {
      setSelectedCannedMessageForStatus('');
      return;
    }

    const order = pendingStatusChange.order;
    if (!order) return;

    const canned = cannedMessages.find(m => m.id === cannedId);
    if (!canned) return;

    // Replace placeholders with actual values - use just the message part for custom message
    let message = canned.message;
    message = message.replace(/{customerName}/g, order.customer_name || 'Customer');
    message = message.replace(/{orderNumber}/g, order.order_number || '');

    setCustomMessage(message);
    setSelectedCannedMessageForStatus(cannedId);
  };

  // Open email modal for follow-up
  const handleOpenEmailModal = () => {
    if (!selectedOrder) return;
    setEmailSubject(`Follow-up regarding Order ${selectedOrder.order_number}`);
    setEmailMessage(`Hello ${selectedOrder.customer_name},\n\nWe wanted to follow up regarding your order ${selectedOrder.order_number}.\n\n`);
    setSelectedCannedMessage('');
    setShowEmailModal(true);
  };

  const applyRefundReasonTemplate = (reason, amount = refundAmount, order = selectedOrder) => {
    if (!order) return;
    const nextLabel = formatRefundAmountLabel(amount);
    setRefundExplanation(
      buildRefundExplanationForReason(reason, order, amount, cannedMessages)
    );
    setRefundExplanationDirty(false);
    lastRefundAmountLabelRef.current = nextLabel;
  };

  // Keep {refundAmount} in the email text in sync when the amount field changes
  useEffect(() => {
    if (!showRefundModal || !selectedOrder) return;

    const nextLabel = formatRefundAmountLabel(refundAmount);
    const prevLabel = lastRefundAmountLabelRef.current;
    if (!nextLabel || nextLabel === prevLabel) return;

    if (!refundExplanationDirty) {
      applyRefundReasonTemplate(refundReason, refundAmount, selectedOrder);
      return;
    }

    setRefundExplanation((current) =>
      syncRefundAmountInExplanation(current, prevLabel, refundAmount)
    );
    lastRefundAmountLabelRef.current = nextLabel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refundAmount, showRefundModal]);

  // Open refund modal
  const handleOpenRefundModal = () => {
    if (!selectedOrder) return;
    const remainingRefundable = Math.max(0, selectedOrder.total - getTotalRefunded(selectedOrder));
    const shippingAmount = Math.min(selectedOrder.shipping || 0, remainingRefundable);
    const defaultRefundAmount = shippingAmount > 0 ? shippingAmount : remainingRefundable;
    const amountStr = (defaultRefundAmount / 100).toFixed(2);
    const defaultReason = 'shipping_refund';
    setRefundAmount(amountStr);
    setRefundReason(defaultReason);
    setRefundExplanation(
      buildRefundExplanationForReason(defaultReason, selectedOrder, amountStr, cannedMessages)
    );
    setRefundExplanationDirty(false);
    lastRefundAmountLabelRef.current = formatRefundAmountLabel(amountStr);
    setSelectedRefundItems([]);
    setRefundMode('amount');
    setShowRefundModal(true);
  };

  // Toggle item selection for refund
  const toggleRefundItem = (itemIndex) => {
    setSelectedRefundItems(prev => {
      if (prev.includes(itemIndex)) {
        return prev.filter(idx => idx !== itemIndex);
      } else {
        return [...prev, itemIndex];
      }
    });
  };

  // Calculate refund amount from selected items
  const calculateRefundFromItems = () => {
    if (!selectedOrder || selectedRefundItems.length === 0) return 0;

    let total = 0;
    selectedRefundItems.forEach(itemIndex => {
      const item = selectedOrder.items[itemIndex];
      if (item) {
        const itemPrice = item.unitPrice || item.price_cents || item.price || 0;
        const quantity = item.quantity || 1;
        total += itemPrice * quantity;
      }
    });

    return total;
  };

  // Update refund amount when items are selected/deselected
  useEffect(() => {
    if (refundMode === 'items' && selectedOrder) {
      const calculatedAmount = calculateRefundFromItems();
      setRefundAmount((calculatedAmount / 100).toFixed(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRefundItems, refundMode, selectedOrder]);

  // Process refund
  const handleProcessRefund = () => {
    if (!selectedOrder) return;
    // Show confirmation modal instead of window.confirm
    setShowRefundConfirmModal(true);
  };

  const confirmRefund = async () => {
    if (!selectedOrder) return;

    setShowRefundConfirmModal(false);

    try {
      setProcessingRefund(true);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Determine payment method
      const isStripe = selectedOrder.payment_method && selectedOrder.payment_method.toLowerCase().includes('stripe');
      const isPayPal = selectedOrder.payment_method && selectedOrder.payment_method.toLowerCase().includes('paypal');

      let endpoint = '';
      if (isStripe) {
        endpoint = '/api/payment/refund-stripe';
      } else if (isPayPal) {
        endpoint = '/api/payment/refund-paypal';
      } else {
        throw new Error('Unable to determine payment method for refund');
      }

      // Prepare refunded items if in items mode
      const refundedItemIndices = refundMode === 'items' ? selectedRefundItems : null;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          ...getAdminPaymentHeaders({ 'Content-Type': 'application/json' }),
        },
        body: JSON.stringify({
          orderId: selectedOrder.id,
          amount: Math.round(parseFloat(refundAmount) * 100), // Convert to cents
          reason: refundReason,
          explanation: refundExplanation,
          refundedItemIndices: refundedItemIndices // Array of item indices that were refunded
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setRefundSuccessResult(result);
        setShowRefundModal(false);
        setShowRefundSuccessModal(true);
        setRefundAmount('');
        setRefundExplanation('');
        setSelectedRefundItems([]);
        // Refresh orders and keep the detail panel in sync
        await fetchOrders(selectedOrder.id, { silent: true });
      } else {
        throw new Error(result.error || 'Failed to process refund');
      }

    } catch (error) {
      console.error('Refund error:', error);
      showError(error.message || 'Failed to process refund');
    } finally {
      setProcessingRefund(false);
    }
  };

  const cancelRefund = () => {
    setShowRefundConfirmModal(false);
  };

  const closeRefundSuccessModal = () => {
    setShowRefundSuccessModal(false);
    setRefundSuccessResult(null);
  };

  const printUpdatedInvoice = async () => {
    if (!selectedOrder) return;
    try {
      const enriched = await ensureOrderEnriched(selectedOrder);
      openPrintWindow(
        generateInvoiceHTML(enriched),
        'Opening updated invoice for printing...'
      );
    } catch (error) {
      console.error('Print updated invoice error:', error);
      showError('Failed to open updated invoice for printing');
    }
  };

  const emailUpdatedInvoice = async () => {
    if (!selectedOrder) return;
    try {
      const enriched = await ensureOrderEnriched(selectedOrder);
      const invoiceHTML = generateInvoiceHTML(enriched);
      const response = await fetch('/api/v1/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: enriched.customer_email,
          from: ORDERS_EMAIL_FROM,
          replyTo: ORDERS_EMAIL_ADDRESS,
          subject: `Updated Invoice for Order ${enriched.order_number}`,
          body: invoiceHTML,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send updated invoice');
      }
      showSuccess(`Updated invoice emailed to ${enriched.customer_email}`);
    } catch (error) {
      console.error('Email updated invoice error:', error);
      showError('Failed to email updated invoice');
    }
  };

  // Send follow-up email to customer
  const handleSendFollowUpEmail = async () => {
    if (!selectedOrder || !emailSubject.trim() || !emailMessage.trim()) {
      showError('Please provide both subject and message');
      return;
    }

    try {
      setSendingEmail(true);

      // Format message with proper line breaks
      const formattedMessage = emailMessage.split('\n').map(line => {
        if (line.trim() === '') return '<br>';
        return `<p style="margin: 0 0 12px 0; line-height: 1.6;">${line}</p>`;
      }).join('');

      const emailHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
        </head>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #0284c7, #0369a1); padding: 30px; text-align: center; border-radius: 8px 8px 0 0;">
            <h1 style="color: white; margin: 0; font-size: 28px;">📧 ${emailSubject}</h1>
            <p style="color: white; margin: 10px 0 0 0; font-size: 16px;">Order #${selectedOrder.order_number}</p>
          </div>
          
          <div style="background: #f9fafb; padding: 20px; border: 1px solid #e5e7eb; border-top: none;">
            <div style="background: white; padding: 20px; border-radius: 6px; margin-bottom: 20px; border: 1px solid #e5e7eb;">
              <div style="font-size: 16px; color: #1f2937; line-height: 1.6;">
                ${formattedMessage}
              </div>
            </div>
            
            <div style="background: #e0f2fe; border-left: 4px solid #0284c7; padding: 16px; border-radius: 6px; margin-bottom: 20px;">
              <h3 style="margin: 0 0 10px 0; color: #075985; font-size: 16px;">📦 Order Information</h3>
              <p style="margin: 5px 0; color: #0c4a6e; line-height: 1.6;">
                <strong>Order Number:</strong> ${selectedOrder.order_number}<br>
                <strong>Customer:</strong> ${selectedOrder.customer_name}<br>
                <strong>Order Date:</strong> ${formatDateOnlyAtlantic(selectedOrder.created_at)}<br>
                <strong>Order Total:</strong> $${(selectedOrder.total / 100).toFixed(2)} CAD
              </p>
            </div>
            
            <div style="text-align: center; padding: 20px; color: #6b7280; font-size: 14px;">
              <p>Questions about your order? Contact us at orders@cranfordpub.ca</p>
              <p style="margin: 10px 0 0 0;">${SITE_TAGLINE_WITH_DASH}</p>
            </div>
          </div>
        </body>
        </html>
      `;

      const response = await fetch('/api/v1/email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to: selectedOrder.customer_email,
          from: ORDERS_EMAIL_FROM,
          replyTo: ORDERS_EMAIL_ADDRESS,
          subject: emailSubject,
          body: emailHTML
        })
      });

      if (response.ok) {
        showSuccess(`Follow-up email sent to ${selectedOrder.customer_email}`);
        setShowEmailModal(false);
        setEmailSubject('');
        setEmailMessage('');
        setSelectedCannedMessage('');
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      showError('Failed to send follow-up email. Please try again.');
      console.error('Send follow-up email error:', error);
    } finally {
      setSendingEmail(false);
    }
  };

  // Export orders to Excel (HTML table format that Excel can open)
  const exportToExcel = (scope = 'all') => {
    const ordersToExport = getOrdersForExport(scope);
    if (!ordersToExport) return;

    // Create HTML table
    let htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8">
        <style>
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #4CAF50; color: white; font-weight: bold; }
          tr:nth-child(even) { background-color: #f2f2f2; }
        </style>
      </head>
      <body>
        <table>
          <thead>
            <tr>
              <th>Order Number</th>
              <th>Date</th>
              <th>Status</th>
              <th>Customer Name</th>
              <th>Customer Email</th>
              <th>Customer Phone</th>
              <th>Shipping Address</th>
              <th>Items</th>
              <th>Subtotal</th>
              <th>Shipping</th>
              <th>Tax</th>
              <th>Total</th>
              <th>Payment Method</th>
              <th>Notes/Messages</th>
            </tr>
          </thead>
          <tbody>
    `;

    ordersToExport.forEach(order => {
      const items = order.items || [];
      const itemsHtml = items.map(item =>
        `${item.name || `Product #${item.id}`} (${item.quantity})`
      ).join('<br>');

      let notesHtml = '';
      try {
        const notes = typeof order.notes_history === 'string'
          ? JSON.parse(order.notes_history)
          : order.notes_history || [];
        notesHtml = notes.map(note =>
          `${formatDateAtlantic(note.timestamp)}<br>${note.status}: ${note.message}${note.sentViaEmail ? ' ✓' : ''}`
        ).join('<br><br>');
      } catch (e) {
        notesHtml = '';
      }

      htmlContent += `
        <tr>
          <td>${order.order_number}</td>
          <td>${formatDateWithTime(order)}</td>
          <td>${order.order_status || order.status || ''}</td>
          <td>${order.customer_name}</td>
          <td>${order.customer_email}</td>
          <td>${order.customer_phone || ''}</td>
          <td>${(order.shipping_address || '').replace(/\n/g, '<br>')}</td>
          <td>${itemsHtml}</td>
          <td>${formatCurrency(order.subtotal)}</td>
          <td>${formatCurrency(order.shipping || 0)}</td>
          <td>${formatCurrency(order.tax || 0)}</td>
          <td>${formatCurrency(order.total)}</td>
          <td>${order.payment_method || 'Credit Card'}</td>
          <td>${notesHtml}</td>
        </tr>
      `;
    });

    htmlContent += `
          </tbody>
        </table>
      </body>
      </html>
    `;

    const scopeLabel = scope === 'selected' ? 'selected' : 'all';
    const filename = `orders_export_${scopeLabel}_${new Date().toISOString().split('T')[0]}.xls`;
    downloadTextFile(htmlContent, filename, 'application/vnd.ms-excel');
    showSuccess(`Exported ${ordersToExport.length} order${ordersToExport.length !== 1 ? 's' : ''} to ${filename}`);
  };

  const parseCsvRows = (text) => {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i += 1) {
      const ch = text[i];
      const next = text[i + 1];
      if (inQuotes) {
        if (ch === '"' && next === '"') {
          field += '"';
          i += 1;
        } else if (ch === '"') {
          inQuotes = false;
        } else {
          field += ch;
        }
      } else if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(field);
        field = '';
      } else if (ch === '\n') {
        row.push(field);
        rows.push(row);
        row = [];
        field = '';
      } else if (ch === '\r') {
        // ignore CR
      } else {
        field += ch;
      }
    }
    if (field.length > 0 || row.length > 0) {
      row.push(field);
      rows.push(row);
    }
    return rows.filter((r) => r.some((cell) => String(cell || '').trim() !== ''));
  };

  const parseMoneyToCents = (value) => {
    const cleaned = String(value ?? '').replace(/[^0-9.-]/g, '');
    if (!cleaned) return 0;
    const dollars = Number.parseFloat(cleaned);
    if (Number.isNaN(dollars)) return 0;
    return Math.round(dollars * 100);
  };

  const parseItemsDetail = (detail) => {
    const text = String(detail || '').trim();
    if (!text) return [];
    return text.split(';').map((part) => part.trim()).filter(Boolean).map((part, index) => {
      const match = part.match(/^(.*?)\s*\(Qty:\s*(\d+)\s*,\s*Price:\s*([^)]+)\)\s*$/i);
      if (match) {
        const unitPrice = parseMoneyToCents(match[3]);
        return {
          id: `imported-${index + 1}`,
          name: match[1].trim(),
          quantity: Number.parseInt(match[2], 10) || 1,
          unitPrice,
          price_cents: unitPrice,
          price: unitPrice,
        };
      }
      return {
        id: `imported-${index + 1}`,
        name: part,
        quantity: 1,
        unitPrice: 0,
        price_cents: 0,
        price: 0,
      };
    });
  };

  const normalizeImportCsvRow = (headerNames, cells) => {
    if (cells.length === headerNames.length) {
      return cells;
    }
    if (cells.length < headerNames.length) {
      return [...cells, ...Array(headerNames.length - cells.length).fill('')];
    }

    const dateIdx = headerNames.findIndex((name) => name.toLowerCase() === 'date');
    if (dateIdx === -1) {
      throw new Error(
        `Row has ${cells.length} columns but expected ${headerNames.length}. ` +
        'Re-export from Admin Orders or ensure quoted fields with commas are intact.'
      );
    }

    const extra = cells.length - headerNames.length;
    const normalized = [];
    for (let headerIndex = 0; headerIndex < headerNames.length; headerIndex += 1) {
      if (headerIndex === dateIdx) {
        normalized.push(cells.slice(dateIdx, dateIdx + 1 + extra).join(',').trim());
      } else if (headerIndex < dateIdx) {
        normalized.push(String(cells[headerIndex] ?? '').trim());
      } else {
        normalized.push(String(cells[headerIndex + extra] ?? '').trim());
      }
    }
    return normalized;
  };

  const parseOrdersCsvText = (text) => {
    const rows = parseCsvRows(text);
    if (rows.length < 2) {
      throw new Error('CSV must include a header row and at least one order.');
    }
    const headerNames = rows[0].map((h) => String(h || '').trim());
    const headers = headerNames.map((h) => h.toLowerCase());
    const idx = (name) => headers.indexOf(name.toLowerCase());
    const required = ['Order Number', 'Customer Name', 'Customer Email', 'Total'];
    for (const name of required) {
      if (idx(name) === -1) {
        throw new Error(`Missing required CSV column: ${name}`);
      }
    }

    return rows.slice(1).map((rawCells, rowIndex) => {
      const cells = normalizeImportCsvRow(headerNames, rawCells);
      const get = (name) => {
        const i = idx(name);
        return i >= 0 ? String(cells[i] ?? '').trim() : '';
      };
      const orderNumber = get('Order Number');
      if (!orderNumber) {
        throw new Error(`Row ${rowIndex + 2}: Order Number is required.`);
      }
      const customerEmail = get('Customer Email');
      if (!customerEmail) {
        throw new Error(`Row ${rowIndex + 2}: Customer Email is required.`);
      }
      if (!customerEmail.includes('@')) {
        throw new Error(
          `Row ${rowIndex + 2}: Customer Email looks invalid ("${customerEmail}"). ` +
          'CSV columns may be misaligned — re-export from Admin Orders or quote the Date field.'
        );
      }
      const status = get('Status') || 'pending';
      return {
        order_number: orderNumber,
        created_at: parseOrderDateFromCsv(get('Date')),
        order_status: status,
        status,
        payment_status: 'paid',
        customer_name: get('Customer Name') || 'Customer',
        customer_email: customerEmail,
        customer_phone: get('Customer Phone') || '',
        shipping_address: get('Shipping Address') || 'Address not provided',
        items: parseItemsDetail(get('Items Detail')),
        subtotal: parseMoneyToCents(get('Subtotal')),
        shipping: parseMoneyToCents(get('Shipping')),
        tax: parseMoneyToCents(get('Tax')),
        total: parseMoneyToCents(get('Total')),
        payment_method: get('Payment Method') || 'Imported',
        notes_history: [
          {
            timestamp: new Date().toISOString(),
            status: 'imported',
            message: get('Notes/Messages') || 'Imported from CSV',
            type: 'system',
          },
        ],
      };
    });
  };

  const handleImportOrdersFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = String(file.name || '').toLowerCase();
    if (!fileName.endsWith('.csv')) {
      showError('Please choose a CSV file exported from Admin Orders (Export → CSV). Excel (.xls) files cannot be imported.');
      if (event.target) event.target.value = '';
      return;
    }

    setImportingOrders(true);
    try {
      const text = await file.text();
      const parsedOrders = parseOrdersCsvText(text);
      if (parsedOrders.length === 0) {
        showError('No orders found in the CSV file.');
        return;
      }

      const confirmed = window.confirm(
        `Import ${parsedOrders.length} order${parsedOrders.length !== 1 ? 's' : ''} from "${file.name}"?\n\nExisting order numbers will be skipped.`
      );
      if (!confirmed) return;

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/import`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orders: parsedOrders, skipDuplicates: true }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Import failed');
      }

      showSuccess(
        result.imported > 0
          ? `Import complete: ${result.imported} imported, ${result.skipped || 0} skipped` +
              (result.failed ? `, ${result.failed} failed` : '')
          : result.skipped > 0
            ? `No new orders imported — ${result.skipped} already exist (duplicate order numbers skipped).`
            : `Import complete: ${result.imported || 0} imported` +
              (result.failed ? `, ${result.failed} failed` : '')
      );
      await fetchOrders();
    } catch (error) {
      console.error('Import orders error:', error);
      showError(error.message || 'Failed to import orders');
    } finally {
      setImportingOrders(false);
      if (event.target) event.target.value = '';
    }
  };

  if (!canManageOrders) {
    return (
      <div className="admin-orders-container">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to manage orders.</p>
        </div>
      </div>
    );
  }

  const statusCounts = getStatusCounts();
  const archivedCartInquiryCount = cartInquiries.filter(isCartInquiryArchived).length;
  const visibleCartInquiries = cartInquiries.filter((inquiry) => {
    if (!showArchivedCartInquiries && isCartInquiryArchived(inquiry)) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const hay = [
        inquiry.reference_id,
        inquiry.customer_name,
        inquiry.customer_email,
        inquiry.personal_message,
      ].join(' ').toLowerCase();
      if (!hay.includes(term)) return false;
    }
    if (dateRange.from || dateRange.to) {
      const inquiryDate = new Date(inquiry.created_at_full || inquiry.created_at);
      if (Number.isNaN(inquiryDate.getTime())) return true;
      if (dateRange.from && inquiryDate < new Date(`${dateRange.from}T00:00:00`)) return false;
      if (dateRange.to && inquiryDate > new Date(`${dateRange.to}T23:59:59`)) return false;
    }
    return true;
  }).sort((a, b) => {
    const getValue = (inquiry) => {
      switch (cartInquirySortColumn) {
        case 'reference':
          return String(inquiry.reference_id || '').toLowerCase();
        case 'date':
          return new Date(inquiry.created_at_full || inquiry.created_at || 0).getTime() || 0;
        case 'customer':
          return String(inquiry.customer_name || inquiry.customer_email || '').toLowerCase();
        case 'items':
          return countDisplayableItems(inquiry.items);
        case 'total':
          return Number(inquiry.total) || 0;
        case 'message':
          return String(inquiry.personal_message || '').toLowerCase();
        case 'status':
          if (isCartInquiryArchived(inquiry)) return 2;
          return getCartInquiryReplies(inquiry).length > 0 ? 1 : 0;
        default:
          return '';
      }
    };
    const aVal = getValue(a);
    const bVal = getValue(b);
    let cmp = 0;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      cmp = aVal - bVal;
    } else {
      cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
    }
    return cartInquirySortDirection === 'asc' ? cmp : -cmp;
  });

  const cartInquirySortState = {
    column: cartInquirySortColumn,
    direction: cartInquirySortDirection,
    onSort: handleCartInquirySort,
  };

  return (
    <div className="admin-orders-container">
      <div className="admin-orders-header">
        <div>
          <h2>{getIcon('cart')} Order Management</h2>
          <p>View and manage customer orders and emailed carts</p>
        </div>
        <div className="header-actions">
          {selectedOrderIds.length > 0 && (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginRight: '1rem',
                padding: '0.5rem 0.75rem',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}>
                <span style={{ fontWeight: '500', color: 'var(--color-text-secondary)' }}>
                  Selected: <strong style={{ color: 'var(--color-primary)' }}>{selectedOrderIds.length}</strong> order{selectedOrderIds.length !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={clearOrderSelection}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-text-secondary)',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    fontSize: '0.875rem'
                  }}
                  title="Clear selection"
                >
                  ✕
                </button>
              </div>
              <div className="action-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '1rem' }}>
                <span className="action-label" style={{ fontSize: '0.9rem', fontWeight: '500' }}>📄 Invoice:</span>
                <button
                  className="btn btn-action"
                  onClick={handlePrintInvoice}
                  title="Print Invoice"
                  style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                >
                  🖨️ Print
                </button>
                <button
                  className="btn btn-action"
                  onClick={handleSaveInvoice}
                  title="Save Invoice"
                  style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                >
                  💾 Save
                </button>
                <button
                  className="btn btn-action"
                  onClick={handleEmailInvoice}
                  title="Email Invoice"
                  style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                >
                  📧 Email
                </button>
              </div>
              <div className="action-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '1rem' }}>
                <span className="action-label" style={{ fontSize: '0.9rem', fontWeight: '500' }}>📦 Shipping Label:</span>
                <button
                  className="btn btn-action"
                  onClick={handlePrintShippingLabel}
                  title="Print Shipping Label"
                  style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                >
                  🖨️ Print
                </button>
                <button
                  className="btn btn-action"
                  onClick={handleSaveShippingLabel}
                  title="Save Shipping Label"
                  style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                >
                  💾 Save
                </button>
              </div>
            </>
          )}
          <a
            href={stripeDashboardHomeUrl('live', paymentConfig)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            title={paymentConfig?.stripeDashboard?.live?.accountId
              ? `Stripe Live (${paymentConfig.stripeDashboard.live.accountId})`
              : 'Open Stripe Live Dashboard'}
          >
            💳 Stripe Live
          </a>
          <a
            href={stripePaymentsListUrl('sandbox', paymentConfig)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            title={paymentConfig?.stripeDashboard?.sandbox?.accountId
              ? `Stripe Test (${paymentConfig.stripeDashboard.sandbox.accountId})`
              : 'Open Stripe Test payments'}
          >
            🧪 Stripe Test
          </a>
          <a
            href="https://www.sandbox.paypal.com/businessmanage/account/aboutBusiness"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#0070ba', color: 'white', border: 'none' }}
            title="Open PayPal Sandbox Dashboard in new tab"
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#005ea6'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#0070ba'}
          >
            🅿️ PayPal Dashboard
          </a>
          <div className="export-dropdown">
            <button type="button" className="btn btn-primary export-btn">
              📥 Export Orders
              {selectedOrderIds.length > 0
                ? ` (${selectedOrderIds.length} selected / ${filteredOrders.length} shown)`
                : filteredOrders.length > 0
                  ? ` (${filteredOrders.length})`
                  : ''}
            </button>
            <div className="export-menu">
              <button type="button" onClick={() => exportToCSV('selected')} className="export-option" disabled={selectedOrderIds.length === 0}>
                📄 CSV — selected
                <span className="export-count">{selectedOrderIds.length} orders</span>
              </button>
              <button type="button" onClick={() => exportToExcel('selected')} className="export-option" disabled={selectedOrderIds.length === 0}>
                📊 Excel — selected
                <span className="export-count">{selectedOrderIds.length} orders</span>
              </button>
              <button type="button" onClick={() => exportToCSV('all')} className="export-option">
                📄 CSV — all shown
                <span className="export-count">{filteredOrders.length} orders</span>
              </button>
              <button type="button" onClick={() => exportToExcel('all')} className="export-option">
                📊 Excel — all shown
                <span className="export-count">{filteredOrders.length} orders</span>
              </button>
              <div className="export-menu-divider" />
              <button
                type="button"
                onClick={() => exportProcessingFees('selected')}
                className="export-option export-option-fees"
                disabled={selectedOrderIds.length === 0 || exportingFees}
              >
                {exportingFees ? '⏳ Exporting fees…' : '💳 Processing fees (tax) — selected'}
                <span className="export-count">{selectedOrderIds.length} orders</span>
              </button>
              <button
                type="button"
                onClick={() => exportProcessingFees('all')}
                className="export-option export-option-fees"
                disabled={filteredOrders.length === 0 || exportingFees}
              >
                {exportingFees ? '⏳ Exporting fees…' : '💳 Processing fees (tax) — all shown'}
                <span className="export-count">{filteredOrders.length} orders</span>
              </button>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setMailOrderDraft(null);
              setShowMailOrderModal(true);
            }}
            title="Create mail/check order with wholesale or custom pricing"
          >
            {MAIL_ORDER_ACTION_LABEL}
          </button>
          <label
            className={`btn btn-secondary import-orders-label${importingOrders ? ' is-disabled' : ''}`}
            title="Import orders from a CSV exported by this page (Export → CSV)"
          >
            {importingOrders ? '⏳ Importing…' : '📤 Import Orders'}
            <input
              type="file"
              accept=".csv,text/csv,application/csv"
              className="import-orders-file-input"
              onChange={handleImportOrdersFile}
              disabled={importingOrders}
            />
          </label>
          <button
            className="btn btn-secondary"
            onClick={() => {
              fetchOrders();
              fetchCartInquiries();
            }}
          >
            {getIcon('refresh')} Refresh
          </button>
        </div>
      </div>

      <div className="merchant-balances-bar">
        <div className="merchant-balances-title">
          Account balances
          <button
            type="button"
            className="merchant-balances-refresh"
            onClick={fetchMerchantBalances}
            disabled={loadingMerchantBalances}
            title="Refresh Stripe and PayPal balances"
          >
            {loadingMerchantBalances ? 'Refreshing…' : '↻ Refresh'}
          </button>
        </div>
        <div className="merchant-balances-chips">
          {merchantBalances ? (
            <>
              {renderMerchantBalanceChip('Stripe Live', merchantBalances.stripe?.live, '💳')}
              {renderMerchantBalanceChip('Stripe Test', merchantBalances.stripe?.sandbox, '🧪')}
              {renderMerchantBalanceChip('PayPal Live', merchantBalances.paypal?.live, '🅿️')}
              {renderMerchantBalanceChip('PayPal Sandbox', merchantBalances.paypal?.sandbox, '🧪')}
            </>
          ) : (
            <span className="merchant-balances-loading">
              {loadingMerchantBalances ? 'Loading balances…' : 'Balances unavailable'}
            </span>
          )}
        </div>
        {merchantBalances?.fetchedAt && (
          <span className="merchant-balances-updated" title={merchantBalances.fetchedAt}>
            Updated {formatTimeOnlyAtlantic(merchantBalances.fetchedAt)}
          </span>
        )}
      </div>

      {/* Filters Section */}
      <div className="orders-filters">
        {/* Status Filter Buttons */}
        <div className="status-filter-buttons">
          <button
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All Orders ({statusCounts.all})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            ⏳ Pending ({statusCounts.pending})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'processing' ? 'active' : ''}`}
            onClick={() => setStatusFilter('processing')}
          >
            ⚙️ Processing ({statusCounts.processing})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'shipped' ? 'active' : ''}`}
            onClick={() => setStatusFilter('shipped')}
          >
            📦 Shipped ({statusCounts.shipped})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'delivered' ? 'active' : ''}`}
            onClick={() => setStatusFilter('delivered')}
          >
            ✅ Delivered ({statusCounts.delivered})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'cancelled' ? 'active' : ''}`}
            onClick={() => setStatusFilter('cancelled')}
          >
            ❌ Cancelled ({statusCounts.cancelled})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'questions' ? 'active' : ''}`}
            onClick={() => setStatusFilter('questions')}
          >
            ❓ Questions ({statusCounts.questions})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'refunded' ? 'active' : ''}`}
            onClick={() => setStatusFilter('refunded')}
          >
            💰 Refunded ({statusCounts.refunded})
          </button>
        </div>

        {/* Search and Date Range Row */}
        <div className="search-and-date-row">
          {/* Search */}
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by order number, customer name, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button
                className="clear-search-btn"
                onClick={() => setSearchTerm('')}
              >
                ✕
              </button>
            )}
          </div>

          {/* Date Range Filter */}
          <div className="date-range-filter">
            <div className="date-input-group">
              <label htmlFor="date-from">From:</label>
              <input
                id="date-from"
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                className="date-input"
              />
            </div>
            <div className="date-input-group">
              <label htmlFor="date-to">To:</label>
              <input
                id="date-to"
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                className="date-input"
              />
            </div>
            {(dateRange.from || dateRange.to) && (
              <button
                className="clear-date-btn"
                onClick={() => setDateRange({ from: '', to: '' })}
                title="Clear date filter"
              >
                ✕ Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {!loading && (
        <section className="cart-inquiries-admin">
          <div className="cart-inquiries-admin-header">
            <div>
              <h3>📧 Email my cart</h3>
              <p>
                Checkout cart inquiries emailed to Paul. These are <strong>not orders</strong> until you create a mail / check order.
              </p>
            </div>
            <div className="cart-inquiries-admin-tools">
              {archivedCartInquiryCount > 0 && (
                <label className="cart-inquiries-show-archived">
                  <input
                    type="checkbox"
                    checked={showArchivedCartInquiries}
                    onChange={(e) => setShowArchivedCartInquiries(e.target.checked)}
                  />
                  Show archived ({archivedCartInquiryCount})
                </label>
              )}
              <span className="cart-inquiries-admin-count">
                {visibleCartInquiries.length}
                {visibleCartInquiries.length !== cartInquiries.length
                  ? ` of ${cartInquiries.length}`
                  : ''}{' '}
                inquir{visibleCartInquiries.length === 1 ? 'y' : 'ies'}
              </span>
            </div>
          </div>
          {visibleCartInquiries.length === 0 ? (
            <div className="cart-inquiries-admin-empty">
              {cartInquiries.length === 0
                ? 'No emailed carts yet.'
                : !showArchivedCartInquiries && archivedCartInquiryCount === cartInquiries.length
                  ? 'All emailed carts are archived. Use Show archived to see them.'
                  : 'No emailed carts match the current search or date filter.'}
            </div>
          ) : (
            <div className="orders-table-container cart-inquiries-admin-table">
              <table className="orders-table">
                <thead>
                  <tr>
                    {renderSortableHeader('reference', 'Reference', cartInquirySortState)}
                    {renderSortableHeader('date', 'Date', cartInquirySortState)}
                    {renderSortableHeader('customer', 'Customer', cartInquirySortState)}
                    {renderSortableHeader('items', 'Items', cartInquirySortState)}
                    {renderSortableHeader('total', 'Total', cartInquirySortState)}
                    {renderSortableHeader('status', 'Status', cartInquirySortState)}
                    {renderSortableHeader('message', 'Message', cartInquirySortState)}
                    <th className="actions-column">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCartInquiries.map((inquiry) => {
                    const itemCount = countDisplayableItems(inquiry.items);
                    const replied = getCartInquiryReplies(inquiry).length > 0;
                    const archived = isCartInquiryArchived(inquiry);
                    return (
                      <tr key={inquiry.id} className={archived ? 'is-archived' : undefined}>
                        <td className="order-number">
                          <button
                            type="button"
                            className="order-number-link"
                            onClick={() => setSelectedCartInquiry(inquiry)}
                            title={`View ${inquiry.reference_id}`}
                          >
                            {inquiry.reference_id}
                          </button>
                        </td>
                        <td title={formatDateWithTime(inquiry)}>
                          {formatDateOnlyAtlantic(inquiry.created_at_full || inquiry.created_at)}
                        </td>
                        <td
                          className="cart-inquiry-customer"
                          title={[inquiry.customer_name, inquiry.customer_email].filter(Boolean).join(' · ')}
                        >
                          <span className="customer-name">{inquiry.customer_name || '—'}</span>
                          {inquiry.customer_email && (
                            <span className="customer-email">{inquiry.customer_email}</span>
                          )}
                        </td>
                        <td className="items-count">{itemCount}</td>
                        <td className="order-total">
                          <strong>{formatCurrency(inquiry.total)}</strong>
                        </td>
                        <td>
                          <span className={`cart-inquiry-badge${archived ? ' is-archived' : replied ? ' is-replied' : ''}`}>
                            {archived ? 'Archived' : replied ? 'Replied' : 'Sent'}
                          </span>
                        </td>
                        <td className="cart-inquiry-message-preview" title={inquiry.personal_message || ''}>
                          {inquiry.personal_message
                            ? inquiry.personal_message
                            : <span className="net-payout-muted">—</span>}
                        </td>
                        <td className="order-actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => setSelectedCartInquiry(inquiry)}
                            title="View cart inquiry details"
                            aria-label={`View ${inquiry.reference_id}`}
                          >
                            {getIcon('eye')} View
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => openCartInquiryReply(inquiry)}
                            title="Reply to the customer by email"
                            aria-label={`Reply to ${inquiry.reference_id}`}
                          >
                            {getIcon('send')} Reply
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => openMailOrderFromInquiry(inquiry)}
                            title="Create a mail / check order from this cart"
                            aria-label={`Create mail order from ${inquiry.reference_id}`}
                          >
                            {getIcon('mail')} Mail
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => archiveCartInquiry(inquiry, !archived)}
                            disabled={archivingCartInquiryId === inquiry.id}
                            title={archived ? 'Restore this cart inquiry' : 'Archive this cart inquiry'}
                            aria-label={`${archived ? 'Restore' : 'Archive'} ${inquiry.reference_id}`}
                          >
                            {getIcon('archive')} {archived ? 'Restore' : 'Archive'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => setCartInquiryToDelete(inquiry)}
                            title="Delete this cart inquiry"
                            aria-label={`Delete ${inquiry.reference_id}`}
                          >
                            {getIcon('delete')} Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Orders List */}
      {loading ? (
        <div className="loading-state">
          <LoadingSpinner size="medium" message="Loading orders..." />
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="empty-state">
          <p>📦 No orders found</p>
          {searchTerm && (
            <button className="btn btn-secondary" onClick={() => setSearchTerm('')}>
              Clear Search
            </button>
          )}
        </div>
      ) : (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th className="select-column">
                  <input
                    type="checkbox"
                    checked={allVisibleOrdersSelected}
                    onChange={toggleSelectAllVisibleOrders}
                    title={allVisibleOrdersSelected ? 'Deselect all visible orders' : 'Select all visible orders'}
                    aria-label={allVisibleOrdersSelected ? 'Deselect all visible orders' : 'Select all visible orders'}
                  />
                </th>
                {renderSortableHeader('order_number', 'Order #')}
                {renderSortableHeader('date', 'Date')}
                {renderSortableHeader('customer', 'Customer')}
                {renderSortableHeader('items', 'Items')}
                {renderSortableHeader('total', 'Total')}
                <th title="Stripe/PayPal processing fee deducted from payout">Proc. fee</th>
                <th>Net payout</th>
                {renderSortableHeader('order_status', 'Order Status')}
                {renderSortableHeader('payment_status', 'Payment Status')}
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => {
                const isSelected = selectedOrderIds.includes(order.id);
                return (
                <tr key={order.id} className={isSelected ? 'selected-row' : ''}>
                  <td className="select-column">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleOrderSelection(order.id)}
                      aria-label={`Select order ${order.order_number}`}
                    />
                  </td>
                  <td className="order-number">
                    <button
                      type="button"
                      className="order-number-link"
                      onClick={() => handleViewDetails(order)}
                      title={`View order ${order.order_number}`}
                    >
                      {order.order_number}
                    </button>
                    {(() => {
                      const badge = getOrderPaymentEnvBadge(order, { paymentConfig });
                      if (!badge) return null;
                      const badgeStyle = getOrderEnvBadgeStyles(badge);
                      return (
                        <div
                          title={badge.title}
                          style={{
                            marginTop: '0.25rem',
                            display: 'inline-block',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            padding: '1px 6px',
                            borderRadius: '4px',
                            ...badgeStyle,
                          }}
                        >
                          {badge.label}
                        </div>
                      );
                    })()}
                  </td>
                  <td>{formatDateWithTime(order)}</td>
                  <td>
                    <div className="customer-info">
                      <div className="customer-name">{order.customer_name}</div>
                      <div className="customer-email">{order.customer_email}</div>
                    </div>
                  </td>
                  <td className="items-count">
                    {(() => {
                      const count = countDisplayableItems(order.items);
                      return `${count} item${count !== 1 ? 's' : ''}`;
                    })()}
                  </td>
                  <td className="order-total">
                    <strong>{formatCurrency(order.total)}</strong>
                  </td>
                  <td className="order-processing-fee">
                    {(() => {
                      if (!order.payment_intent_id && !order.paypal_order_id) {
                        return <span className="net-payout-muted">—</span>;
                      }
                      const summary = paymentSummaries[order.id];
                      if (loadingPaymentSummaries && !summary) {
                        return <span className="net-payout-muted">…</span>;
                      }
                      if (summary?.error) {
                        return <span className="net-payout-muted" title={summary.error}>—</span>;
                      }
                      if (!summary?.processingFeeFormatted) {
                        return <span className="net-payout-muted">—</span>;
                      }
                      const providerLabel = summary.provider === 'paypal' ? 'PayPal' : 'Stripe';
                      const envLabel = summary.mode === 'sandbox' ? 'Test' : 'Live';
                      return (
                        <div className="net-payout-cell">
                          <strong>{summary.processingFeeFormatted.replace(' CAD', '')}</strong>
                          <span className="net-payout-meta" title={`${providerLabel} ${envLabel} processing fee`}>
                            {providerLabel} {envLabel}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="order-net-payout">
                    {(() => {
                      if (!order.payment_intent_id && !order.paypal_order_id) {
                        return <span className="net-payout-muted">—</span>;
                      }
                      const summary = paymentSummaries[order.id];
                      if (loadingPaymentSummaries && !summary) {
                        return <span className="net-payout-muted">…</span>;
                      }
                      if (summary?.error) {
                        return <span className="net-payout-muted" title={summary.error}>—</span>;
                      }
                      if (!summary?.netAmountFormatted) {
                        return <span className="net-payout-muted">—</span>;
                      }
                      const providerLabel = summary.provider === 'paypal' ? 'PayPal' : 'Stripe';
                      const envLabel = summary.mode === 'sandbox' ? 'Test' : 'Live';
                      return (
                        <div className="net-payout-cell">
                          <strong>{summary.netAmountFormatted.replace(' CAD', '')}</strong>
                          <span className="net-payout-meta" title={`${providerLabel} ${envLabel} · gross ${summary.paymentAmountFormatted || '—'} · fee ${summary.processingFeeFormatted || '—'}`}>
                            {providerLabel} {envLabel}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td>
                    <select
                      className={getStatusBadgeClass(order.order_status || order.status)}
                      value={order.order_status || order.status || 'pending'}
                      onChange={(e) => handleStatusChange(order.id, e.target.value, order.order_status || order.status, 'order')}
                      disabled={updatingStatus}
                    >
                      <option value="pending">⏳ Pending</option>
                      <option value="processing">⚙️ Processing</option>
                      <option value="shipped">📦 Shipped</option>
                      <option value="delivered">✅ Delivered</option>
                      <option value="completed">✔️ Completed</option>
                      <option value="cancelled">❌ Cancelled</option>
                      <option value="inquiry">💬 Inquiry</option>
                      <option value="questions">❓ Questions</option>
                    </select>
                  </td>
                  <td>
                    <span className={getPaymentStatusBadgeClass(order.payment_status || 'paid')}>
                      {(order.payment_status || 'paid') === 'paid' ? '✅' : (order.payment_status || 'paid') === 'refunded' ? '💰' : (order.payment_status || 'paid') === 'partially_refunded' ? '💵' : '⏳'} {(order.payment_status || 'paid').charAt(0).toUpperCase() + (order.payment_status || 'paid').slice(1).replace('_', ' ')}
                    </span>
                  </td>
                  <td className="order-actions">
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => handleViewDetails(order)}
                      style={{ marginRight: '0.25rem' }}
                      title="View order details"
                      aria-label={`View ${order.order_number}`}
                    >
                      {getIcon('eye')} View
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteOrder(order)}
                      title="Delete this order"
                      aria-label={`Delete ${order.order_number}`}
                    >
                      {getIcon('delete')} Delete
                    </button>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Order Details Modal */}
      {showDetailsModal && selectedOrder && (
        <div className="modal-overlay" onClick={handleCloseDetails}>
          <div className="modal-content order-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Order Details: {selectedOrder.order_number}</h3>
              <button className="close-btn" onClick={handleCloseDetails}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Top Section: Status and Metadata in one row */}
              <div className="detail-section-horizontal">
                <div className="status-update-section" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <div>
                    <label style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem', display: 'block' }}>
                      Order Status (Fulfillment)
                    </label>
                    <span className={getStatusBadgeClass(selectedOrder.order_status || selectedOrder.status)}>
                      {getStatusIcon(selectedOrder.order_status || selectedOrder.status)} {(selectedOrder.order_status || selectedOrder.status || 'pending').charAt(0).toUpperCase() + (selectedOrder.order_status || selectedOrder.status || 'pending').slice(1)}
                    </span>
                    <select
                      value={selectedOrder.order_status || selectedOrder.status || 'pending'}
                      onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value, selectedOrder.order_status || selectedOrder.status, 'order')}
                      disabled={updatingStatus}
                      className="status-select"
                      style={{ marginLeft: '0.5rem' }}
                    >
                      <option value="pending">⏳ Pending</option>
                      <option value="processing">⚙️ Processing</option>
                      <option value="shipped">📦 Shipped</option>
                      <option value="delivered">✅ Delivered</option>
                      <option value="completed">✔️ Completed</option>
                      <option value="inquiry">💬 Customer Inquiry</option>
                      <option value="questions">❓ Questions</option>
                      <option value="cancelled">❌ Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem', display: 'block' }}>
                      Payment Status
                    </label>
                    <span className={getPaymentStatusBadgeClass(selectedOrder.payment_status || 'paid')}>
                      {(selectedOrder.payment_status || 'paid') === 'paid' ? '✅' : (selectedOrder.payment_status || 'paid') === 'refunded' ? '💰' : (selectedOrder.payment_status || 'paid') === 'partially_refunded' ? '💵' : '⏳'} {(selectedOrder.payment_status || 'paid').charAt(0).toUpperCase() + (selectedOrder.payment_status || 'paid').slice(1).replace('_', ' ')}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginLeft: '0.5rem', fontStyle: 'italic' }}>
                      (Updated automatically by payment actions)
                    </span>
                    {(selectedOrder.payment_status || 'paid') === 'pending_payment' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        style={{ marginLeft: '0.75rem' }}
                        onClick={() => handleMarkPaymentReceived(selectedOrder)}
                        disabled={markingPaymentReceived}
                      >
                        {markingPaymentReceived ? 'Updating…' : '✅ Mark payment received'}
                      </button>
                    )}
                  </div>
                </div>
                <div className="order-meta-inline">
                  <span><strong>Date:</strong> {formatDateWithTime(selectedOrder)}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <strong>Payment:</strong>{' '}
                    {formatOrderPaymentLabel(selectedOrder, { paymentConfig })}
                    {(() => {
                      const badge = getOrderPaymentEnvBadge(selectedOrder, { paymentConfig });
                      if (!badge) return null;
                      const badgeStyle = getOrderEnvBadgeStyles(badge);
                      return (
                        <span
                          title={badge.title}
                          style={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: '4px',
                            ...badgeStyle,
                          }}
                        >
                          {badge.label}
                        </span>
                      );
                    })()}
                  </span>
                  {selectedOrder.payment_intent_id && (() => {
                    const orderStripeMode = paymentBreakdown?.stripeMode
                      || resolveOrderStripeMode(selectedOrder, { paymentConfig });
                    const stripeUrl = paymentBreakdown?.stripeDashboardUrl
                      || stripePaymentDetailUrl(selectedOrder.payment_intent_id, orderStripeMode, paymentConfig);
                    const isSandbox = orderStripeMode !== 'live';
                    return (
                      <a
                        href={stripeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#635bff',
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          marginLeft: '0.5rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '4px 8px',
                          backgroundColor: isSandbox ? '#fffbeb' : '#f6f9fc',
                          borderRadius: '4px',
                          border: isSandbox ? '1px solid #fcd34d' : '1px solid #e3e8ef',
                        }}
                        title={`View in Stripe ${isSandbox ? 'Test' : 'Live'}: ${selectedOrder.payment_intent_id}`}
                      >
                        {isSandbox ? '🧪 Stripe Test' : '💳 Stripe Live'}
                        {selectedOrder.stripe_mode && (
                          <span style={{ fontSize: '0.7rem', opacity: 0.85 }}>(saved)</span>
                        )}
                      </a>
                    );
                  })()}
                  {!selectedOrder.payment_intent_id && isStripePaymentMethod(selectedOrder.payment_method) && (() => {
                    const orderStripeMode = selectedOrder.stripe_mode === 'sandbox'
                      ? 'sandbox'
                      : selectedOrder.stripe_mode === 'live'
                        ? 'live'
                        : 'live';
                    const searchQuery = selectedOrder.customer_email || selectedOrder.order_number;
                    const stripeUrl = stripeDashboardSearchUrl(searchQuery, orderStripeMode, paymentConfig);
                    const isSandbox = orderStripeMode !== 'live';
                    return (
                      <a
                        href={stripeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#635bff',
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          marginLeft: '0.5rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '4px 8px',
                          backgroundColor: isSandbox ? '#fffbeb' : '#f6f9fc',
                          borderRadius: '4px',
                          border: isSandbox ? '1px solid #fcd34d' : '1px solid #e3e8ef',
                        }}
                        title={`No Stripe payment ID on file — search Stripe ${isSandbox ? 'Test' : 'Live'} for ${searchQuery}`}
                      >
                        {isSandbox ? '🧪 Search Stripe Test' : '💳 Search Stripe Live'}
                      </a>
                    );
                  })()}
                  {selectedOrder.paypal_order_id && (() => {
                    const orderPayPalMode = selectedOrder.paypal_mode === 'sandbox'
                      ? 'sandbox'
                      : selectedOrder.paypal_mode === 'live'
                        ? 'live'
                        : resolveOrderPayPalMode(selectedOrder, { paymentConfig });
                    const isSandbox = orderPayPalMode !== 'live';
                    const paypalUrl = paypalTransactionUrl(selectedOrder.paypal_order_id, orderPayPalMode);
                    return (
                      <a
                        href={paypalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#0070ba',
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          marginLeft: '0.5rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '4px 8px',
                          backgroundColor: isSandbox ? '#fffbeb' : '#f5f7fa',
                          borderRadius: '4px',
                          border: isSandbox ? '1px solid #fcd34d' : '1px solid #cbd2d9',
                        }}
                        title={`View in PayPal ${isSandbox ? 'Sandbox' : 'Live'}: ${selectedOrder.paypal_order_id}`}
                      >
                        {isSandbox ? '🅿️ PayPal Sandbox' : '🅿️ PayPal Live'}
                        {selectedOrder.paypal_mode && (
                          <span style={{ fontSize: '0.7rem', opacity: 0.85 }}>(saved)</span>
                        )}
                      </a>
                    );
                  })()}
                  {!selectedOrder.paypal_order_id && isPayPalPaymentMethod(selectedOrder.payment_method) && (() => {
                    const orderPayPalMode = selectedOrder.paypal_mode === 'sandbox'
                      ? 'sandbox'
                      : selectedOrder.paypal_mode === 'live'
                        ? 'live'
                        : 'live';
                    const isSandbox = orderPayPalMode !== 'live';
                    const searchHint = selectedOrder.customer_email || selectedOrder.order_number;
                    const paypalUrl = paypalActivityUrl(orderPayPalMode);
                    return (
                      <a
                        href={paypalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#0070ba',
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          marginLeft: '0.5rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '4px 8px',
                          backgroundColor: isSandbox ? '#fffbeb' : '#f5f7fa',
                          borderRadius: '4px',
                          border: isSandbox ? '1px solid #fcd34d' : '1px solid #cbd2d9',
                        }}
                        title={`No PayPal transaction ID on file — open PayPal ${isSandbox ? 'Sandbox' : 'Live'} activity and search for ${searchHint}`}
                      >
                        {isSandbox ? '🅿️ Search PayPal Sandbox' : '🅿️ Search PayPal Live'}
                      </a>
                    );
                  })()}
                </div>
              </div>

              {/* Payment Transaction Details */}
              {(selectedOrder.payment_intent_id || selectedOrder.paypal_order_id) && (
                <div style={{
                  padding: '16px',
                  backgroundColor: 'var(--color-surface)',
                  borderRadius: '8px',
                  border: '1px solid var(--color-primary)',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--color-primary)' }}>
                    💳 Payment Transaction Details
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ color: 'var(--color-text-primary)' }}>Internal Order #:</strong>
                      <span style={{
                        fontFamily: 'monospace',
                        padding: '4px 8px',
                        backgroundColor: 'var(--color-surface)',
                        borderRadius: '4px',
                        border: '1px solid var(--color-primary)',
                        color: 'var(--color-text-primary)'
                      }}>
                        {selectedOrder.order_number}
                      </span>
                    </div>
                    {selectedOrder.payment_intent_id && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong style={{ color: 'var(--color-text-primary)' }}>Stripe Payment Intent:</strong>
                        <span style={{
                          fontFamily: 'monospace',
                          padding: '4px 8px',
                          backgroundColor: 'var(--color-surface)',
                          borderRadius: '4px',
                          border: '1px solid var(--color-primary)',
                          fontSize: '12px',
                          color: 'var(--color-text-primary)'
                        }}>
                          {selectedOrder.payment_intent_id}
                        </span>
                      </div>
                    )}
                    {selectedOrder.paypal_order_id && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong style={{ color: 'var(--color-text-primary)' }}>PayPal Transaction:</strong>
                        <span style={{
                          fontFamily: 'monospace',
                          padding: '4px 8px',
                          backgroundColor: 'var(--color-surface)',
                          borderRadius: '4px',
                          border: '1px solid var(--color-primary)',
                          fontSize: '12px',
                          color: 'var(--color-text-primary)'
                        }}>
                          {selectedOrder.paypal_order_id}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Payment Breakdown */}
                  {(selectedOrder.payment_intent_id || selectedOrder.paypal_order_id) && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--color-border)' }}>
                      <h5 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: 'var(--color-text-primary)' }}>
                        Payment breakdown
                        {paymentBreakdown?.provider && (
                          <span style={{ marginLeft: '8px', fontWeight: '500', fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                            ({paymentBreakdown.provider === 'paypal' ? 'PayPal' : 'Stripe'})
                          </span>
                        )}
                      </h5>
                      {loadingBreakdown ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                          <LoadingSpinner size="small" message="Loading breakdown..." />
                        </div>
                      ) : paymentBreakdown ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: 'var(--color-text-primary)' }}>Payment amount</span>
                            <strong style={{ color: 'var(--color-text-primary)' }}>{paymentBreakdown.paymentAmountFormatted}</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ color: 'var(--color-text-secondary)' }}>
                                {paymentBreakdown.feeLabel || (paymentBreakdown.provider === 'paypal' ? 'PayPal processing fees' : 'Stripe processing fees')}
                              </span>
                            </div>
                            <span style={{ color: 'var(--color-error)' }}>
                              - {paymentBreakdown.processingFeeFormatted || paymentBreakdown.stripeFeeFormatted}
                            </span>
                          </div>
                          {paymentBreakdown.totalRefunded > 0 && (
                            <>
                              {paymentBreakdown.refunds.map((refund, index) => (
                                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <span style={{ color: 'var(--color-text-secondary)' }}>Refunded amount</span>
                                    {refund.explanation && (
                                      <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                                        {refund.explanation.split('\n')[0].substring(0, 50)}...
                                      </span>
                                    )}
                                  </div>
                                  <span style={{ color: 'var(--color-error)' }}>- {refund.amountFormatted}</span>
                                </div>
                              ))}
                            </>
                          )}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingTop: '8px',
                            marginTop: '8px',
                            borderTop: '1px solid var(--color-border)',
                            fontWeight: '600'
                          }}>
                            <span style={{ color: 'var(--color-text-primary)' }}>Net amount</span>
                            <strong style={{ color: 'var(--color-primary)' }}>{paymentBreakdown.netAmountFormatted}</strong>
                          </div>
                        </div>
                      ) : (
                        <div style={{ color: 'var(--color-text-secondary)', fontSize: '12px' }}>
                          Unable to load payment breakdown
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Two Column Layout for Customer Info and Address */}
              <div className="detail-section-two-column">
                <div className="detail-column">
                  <h4>Customer Information</h4>
                  <div className="info-compact">
                    <div><strong>Name:</strong> {selectedOrder.customer_name}</div>
                    <div><strong>Email:</strong> {selectedOrder.customer_email}</div>
                    {selectedOrder.customer_phone && (
                      <div><strong>Phone:</strong> {selectedOrder.customer_phone}</div>
                    )}
                  </div>
                </div>
                <div className="detail-column">
                  <h4>Shipping Address</h4>
                  <div className="address-block-compact">
                    {selectedOrder.shipping_address}
                  </div>
                </div>
              </div>

              {/* Tracking Number Section */}
              <div style={{
                padding: '16px',
                backgroundColor: 'var(--color-surface)',
                borderRadius: '8px',
                border: '1px solid var(--color-border)',
                marginBottom: '20px'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--color-text-primary)' }}>
                  📦 Shipping Tracking Number
                </h4>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <input
                    type="text"
                    value={selectedOrder.tracking_number || ''}
                    onChange={async (e) => {
                      const newTrackingNumber = e.target.value;
                      // Update local state immediately for UI responsiveness
                      setSelectedOrder({ ...selectedOrder, tracking_number: newTrackingNumber });

                      // Save to backend
                      try {
                        const token = localStorage.getItem('token');
                        const response = await fetch(`${API_URL}/${selectedOrder.id}`, {
                          method: 'PUT',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            status: selectedOrder.status,
                            tracking_number: newTrackingNumber.trim() || null
                          })
                        });

                        if (!response.ok) {
                          throw new Error('Failed to update tracking number');
                        }

                        // Update orders list
                        setOrders(orders.map(order =>
                          order.id === selectedOrder.id ? { ...order, tracking_number: newTrackingNumber.trim() || null } : order
                        ));
                      } catch (err) {
                        console.error('Error updating tracking number:', err);
                        showError('Failed to update tracking number');
                        // Revert local state on error
                        setSelectedOrder({ ...selectedOrder, tracking_number: selectedOrder.tracking_number });
                      }
                    }}
                    placeholder="Enter tracking number (e.g., 1Z999AA10123456784)"
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      border: '2px solid var(--color-border)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'var(--color-input-bg))',
                      color: 'var(--color-text-primary)',
                      fontFamily: 'monospace',
                      letterSpacing: '0.5px'
                    }}
                  />
                </div>
                {selectedOrder.tracking_number && (
                  <p style={{
                    fontSize: '0.8rem',
                    color: 'var(--color-text-secondary)',
                    marginTop: '0.5rem',
                    fontStyle: 'italic'
                  }}>
                    This tracking number will be included in shipping notification emails.
                  </p>
                )}
              </div>

              {/* Order Items and Summary Side by Side */}
              <div className="detail-section-two-column">
                <div className="detail-column">
                  <h4>
                    <span>Order Items ({selectedItems.length}/{countDisplayableItems(selectedOrder.items)} selected)</span>
                    <span
                      className="select-all-items"
                      onClick={selectedItems.length === selectedOrder.items.length ? deselectAllItems : selectAllItems}
                    >
                      {selectedItems.length === selectedOrder.items.length ? '✕ Deselect All' : '✓ Select All'}
                    </span>
                  </h4>
                  <div className="items-list-compact">
                    {selectedOrder.items.map((item, index) => {
                      // Check for bundle in multiple ways
                      const isBundle = item.isBundle || item.is_bundle || false;
                      const isPartOfBundle = item.isPartOfBundle || false;
                      // Also check if bundleProducts array exists (even if isBundle flag is missing)
                      const hasBundleProducts = item.bundleProducts && Array.isArray(item.bundleProducts) && item.bundleProducts.length > 0;
                      const actualIsBundle = isBundle || hasBundleProducts;

                      // Skip individual bundle items - they'll be shown hierarchically under the bundle
                      if (isPartOfBundle && !actualIsBundle) {
                        return null;
                      }

                      // Check if this bundle has expanded products
                      const expandedProducts = selectedOrder.items.filter(i => i.isPartOfBundle && i.bundleParentId === item.id);
                      const hasExpandedProducts = expandedProducts.length > 0;

                      const bundleProducts = item.bundleProducts || [];
                      const bundleDiscount = item.bundleDiscount || item.bundle_discount || 0;
                      const originalPrice = item.originalPrice || item.original_price || 0;
                      const itemPrice = item.unitPrice || item.price_cents || item.price || 0;

                      // Calculate bundle savings if applicable
                      let bundleSavings = 0;
                      if (actualIsBundle && bundleDiscount > 0 && originalPrice > 0) {
                        bundleSavings = (originalPrice - itemPrice) * item.quantity;
                      }

                      // Use expanded products if available, otherwise use bundleProducts
                      const productsToDisplay = hasExpandedProducts ? expandedProducts : bundleProducts;

                      // Display individual bundle items (for packing) indented under the bundle - REMOVED
                      // This section is now removed as we show items hierarchically
                      if (false) {
                        // Find the bundle parent to get its price
                        const bundleParent = selectedOrder.items.find(i =>
                          (i.isBundle || i.is_bundle || (i.bundleProducts && Array.isArray(i.bundleProducts) && i.bundleProducts.length > 0)) &&
                          i.id === item.bundleParentId
                        );
                        const bundleParentPrice = bundleParent
                          ? (bundleParent.unitPrice || bundleParent.price_cents || bundleParent.price || 0) * (bundleParent.quantity || 1)
                          : 0;

                        return (
                          <div
                            key={index}
                            className={`item-row-compact ${selectedItems.includes(index) ? 'selected' : ''}`}
                            style={{
                              paddingLeft: '2rem',
                              borderLeft: `3px solid var(--color-primary)`,
                              backgroundColor: 'color-mix(in srgb, var(--color-primary) 5%, transparent)',
                              marginBottom: '4px',
                              borderRadius: '6px'
                            }}
                          >
                            <div className="item-checkbox-wrapper">
                              <input
                                type="checkbox"
                                className="item-checkbox"
                                checked={selectedItems.includes(index)}
                                onChange={() => toggleItemSelection(index)}
                              />
                            </div>
                            <div className="item-info">
                              <strong style={{ color: 'var(--color-text-primary)' }}>{item.name || `Product #${item.id}`}</strong>
                              <span style={{
                                fontSize: '11px',
                                color: 'var(--color-text-secondary)',
                                marginLeft: '8px',
                                fontStyle: 'italic'
                              }}>
                                (Part of: {item.bundleParentName || 'Bundle'})
                              </span>
                              <span className="item-quantity" style={{ color: 'var(--color-text-secondary)' }}>× {item.quantity}</span>
                            </div>
                            <div className="item-price" style={{
                              color: 'var(--color-text-secondary)',
                              fontSize: '0.9em',
                              fontStyle: 'italic'
                            }}>
                              {bundleParentPrice > 0 ? formatCurrency(bundleParentPrice) : 'Packing Item'}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={index}
                          className={`item-row-compact ${selectedItems.includes(index) ? 'selected' : ''}`}
                          style={{
                            backgroundColor: actualIsBundle ? 'color-mix(in srgb, var(--color-primary) 3%, transparent)' : 'transparent',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '8px',
                            border: actualIsBundle ? `2px solid var(--color-primary)` : '1px solid var(--color-border)'
                          }}
                        >
                          <div className="item-checkbox-wrapper">
                            <input
                              type="checkbox"
                              className="item-checkbox"
                              checked={selectedItems.includes(index)}
                              onChange={() => toggleItemSelection(index)}
                            />
                          </div>
                          <div className="item-info" style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: actualIsBundle && productsToDisplay.length > 0 ? '8px' : '0' }}>
                              <strong style={{ color: 'var(--color-text-primary)', fontSize: '15px' }}>
                                {item.name || `Product #${item.id}`}
                              </strong>
                              {actualIsBundle && (
                                <span style={{
                                  background: 'linear-gradient(135deg, var(--color-primary), var(--color-primary-light))',
                                  color: '#ffffff',
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  display: 'inline-block'
                                }}>
                                  📦 BUNDLE
                                </span>
                              )}
                              <span className="item-quantity" style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>
                                × {item.quantity}
                              </span>
                            </div>

                            {/* Variant Display */}
                            {(item.selectedSize || item.selectedColor) && (
                              <div className="item-variants" style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginTop: '4px', fontStyle: 'italic' }}>
                                {item.selectedSize && <span style={{ marginRight: '10px' }}>Size: <strong>{item.selectedSize}</strong></span>}
                                {item.selectedColor && <span>Color: <strong>{item.selectedColor}</strong></span>}
                              </div>
                            )}

                            {/* Hierarchical list of bundle items */}
                            {actualIsBundle && productsToDisplay.length > 0 && (
                              <div style={{
                                marginTop: '8px',
                                paddingLeft: '1.5rem',
                                borderLeft: `2px solid var(--color-primary)`,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px'
                              }}>
                                {productsToDisplay.map((bp, idx) => {
                                  const productName = bp.name || bp.title || `Product #${bp.id || bp.productId || ''}`;
                                  return (
                                    <div key={idx} style={{
                                      fontSize: '13px',
                                      color: 'var(--color-text-secondary)',
                                      lineHeight: '1.5'
                                    }}>
                                      {productName}
                                      {(bp.selectedSize || bp.selectedColor) && (
                                        <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.9em', marginLeft: '4px', fontStyle: 'italic' }}>
                                          - {[
                                            bp.selectedSize && `Size: ${bp.selectedSize}`,
                                            bp.selectedColor && `Color: ${bp.selectedColor}`
                                          ].filter(Boolean).join(', ')}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {actualIsBundle && bundleSavings > 0 && (
                              <div style={{
                                fontSize: '13px',
                                color: 'var(--color-success)',
                                fontWeight: '600',
                                marginTop: '8px',
                                padding: '6px 10px',
                                backgroundColor: 'color-mix(in srgb, var(--color-success) 10%, transparent)',
                                borderRadius: '6px',
                                display: 'inline-block'
                              }}>
                                🎉 Save {formatCurrency(bundleSavings)} ({bundleDiscount}% off)
                              </div>
                            )}
                          </div>
                          <div className="item-price" style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: actualIsBundle ? 'var(--color-primary)' : 'var(--color-text-primary)',
                            minWidth: '100px',
                            textAlign: 'right'
                          }}>
                            {formatCurrency(itemPrice * item.quantity)}
                          </div>
                        </div>
                      );
                    })}

                    {/* Add refunds as line items */}
                    {(() => {
                      let refunds = [];
                      try {
                        const notesHistory = selectedOrder.notes_history
                          ? (typeof selectedOrder.notes_history === 'string'
                            ? JSON.parse(selectedOrder.notes_history)
                            : selectedOrder.notes_history)
                          : [];
                        refunds = notesHistory.filter(note => note.type === 'refund');
                      } catch (e) {
                        refunds = [];
                      }

                      return refunds.map((refund, refundIndex) => {
                        // Extract explanation from refund message
                        let explanation = '';
                        if (refund.explanation) {
                          explanation = refund.explanation;
                        } else if (refund.message) {
                          // Try to extract explanation from message if it contains "Explanation:"
                          const explanationMatch = refund.message.match(/Explanation:\s*(.+)/i);
                          if (explanationMatch) {
                            explanation = explanationMatch[1].trim();
                          } else {
                            // If no "Explanation:" prefix, use the full message (excluding "Refund processed: $X CAD")
                            explanation = refund.message.replace(/^Refund processed:.*?CAD\s*/i, '').trim();
                          }
                        }

                        return (
                          <div
                            key={`refund-${refundIndex}`}
                            className="item-row-compact refund-item"
                            style={{
                              borderLeft: '3px solid var(--color-error)',
                              backgroundColor: 'color-mix(in srgb, var(--color-error) 5%, transparent)',
                              opacity: 0.9
                            }}
                          >
                            <div className="item-checkbox-wrapper">
                              <span style={{ color: 'var(--color-error)', fontSize: '16px' }}>💰</span>
                            </div>
                            <div className="item-info" style={{ flex: 1 }}>
                              <strong style={{ color: 'var(--color-error)' }}>
                                Refund: {refund.refundAmountFormatted || `$${((refund.refundAmount || 0) / 100).toFixed(2)} CAD`}
                              </strong>
                              {explanation && (
                                <div style={{
                                  fontSize: '13px',
                                  color: 'var(--color-text-primary)',
                                  marginTop: '4px',
                                  lineHeight: '1.4',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}>
                                  {explanation}
                                </div>
                              )}
                              <span className="item-quantity" style={{
                                fontSize: '11px',
                                color: 'var(--color-text-secondary)',
                                display: 'block',
                                marginTop: '4px'
                              }}>
                                {formatDateOnlyAtlantic(refund.timestamp)} {formatTimeOnlyAtlantic(refund.timestamp)}
                              </span>
                            </div>
                            <div className="item-price" style={{ color: 'var(--color-error)' }}>
                              - {refund.refundAmountFormatted || `$${((refund.refundAmount || 0) / 100).toFixed(2)} CAD`}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
                <div className="detail-column">
                  <h4 style={{ color: 'var(--color-text-primary)', marginBottom: '16px' }}>Order Summary</h4>
                  <div className="summary-compact" style={{
                    backgroundColor: 'var(--color-surface)',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid var(--color-border)'
                  }}>
                    {/* Calculate total bundle savings */}
                    {(() => {
                      let totalBundleSavings = 0;
                      selectedOrder.items.forEach(item => {
                        const isBundle = item.isBundle || item.is_bundle || false;
                        const hasBundleProducts = item.bundleProducts && Array.isArray(item.bundleProducts) && item.bundleProducts.length > 0;
                        const actualIsBundle = isBundle || hasBundleProducts;
                        const hasExpandedProducts = selectedOrder.items.some(i => i.isPartOfBundle && i.bundleParentId === item.id);

                        if (actualIsBundle && hasExpandedProducts) {
                          const bundleItems = selectedOrder.items.filter(i => i.isPartOfBundle && i.bundleParentId === item.id);
                          const bundleSubtotal = bundleItems.reduce((sum, bi) => sum + ((bi.unitPrice || bi.price_cents || bi.price || 0) * (bi.quantity || 1)), 0);
                          const bundleTotal = (item.unitPrice || item.price_cents || item.price || 0) * (item.quantity || 1);
                          if (bundleSubtotal > bundleTotal) {
                            totalBundleSavings += bundleSubtotal - bundleTotal;
                          }
                        } else if (actualIsBundle && item.bundleDiscount > 0 && item.originalPrice > 0) {
                          const bundleSavings = (item.originalPrice - (item.unitPrice || item.price_cents || item.price || 0)) * (item.quantity || 1);
                          totalBundleSavings += bundleSavings;
                        }
                      });

                      return totalBundleSavings > 0 ? (
                        <div className="summary-row" style={{
                          padding: '10px',
                          backgroundColor: 'color-mix(in srgb, var(--color-success) 10%, transparent)',
                          borderRadius: '6px',
                          marginBottom: '12px',
                          border: `1px solid var(--color-success)`
                        }}>
                          <span style={{ color: 'var(--color-success)', fontWeight: '600' }}>
                            🎉 Total Bundle Savings:
                          </span>
                          <span style={{ color: 'var(--color-success)', fontWeight: '600' }}>
                            -{formatCurrency(totalBundleSavings)}
                          </span>
                        </div>
                      ) : null;
                    })()}

                    <div className="summary-row" style={{ color: 'var(--color-text-primary)' }}>
                      <span style={{ color: 'var(--color-text-secondary)' }}>Subtotal:</span>
                      <span style={{ fontWeight: '500', color: 'var(--color-text-primary)' }}>{formatCurrency(selectedOrder.subtotal)}</span>
                    </div>
                    {selectedOrder.shipping > 0 && (
                      <div className="summary-row" style={{ color: 'var(--color-text-primary)' }}>
                        <span style={{ color: 'var(--color-text-secondary)' }}>Shipping:</span>
                        <span style={{ fontWeight: '500', color: 'var(--color-text-primary)' }}>{formatCurrency(selectedOrder.shipping)}</span>
                      </div>
                    )}
                    {(() => {
                      let sd = null;
                      try { sd = selectedOrder.shipping_details_json ? JSON.parse(selectedOrder.shipping_details_json) : null; } catch(e) {}
                      if (!sd) return null;
                      return (
                        <div className="summary-row" style={{ color: 'var(--color-text-primary)', fontSize: '0.85rem' }}>
                          <span style={{ color: 'var(--color-text-secondary)' }}>Shipping Info:</span>
                          <span style={{ fontWeight: '500' }}>
                            {sd.bandLabel || sd.method || ''}{sd.totalWeightKg ? ` (${sd.totalWeightKg.toFixed(2)} kg)` : ''}
                          </span>
                        </div>
                      );
                    })()}
                    {selectedOrder.tax > 0 && (
                      <div className="summary-row" style={{ color: 'var(--color-text-primary)' }}>
                        <span style={{ color: 'var(--color-text-secondary)' }}>Tax:</span>
                        <span style={{ fontWeight: '500', color: 'var(--color-text-primary)' }}>{formatCurrency(selectedOrder.tax)}</span>
                      </div>
                    )}
                    <div className="summary-row total" style={{
                      borderTop: `2px solid var(--color-border)`,
                      marginTop: '12px',
                      paddingTop: '12px',
                      color: 'var(--color-text-primary)'
                    }}>
                      <span style={{ fontSize: '16px', fontWeight: '600' }}>Total:</span>
                      <span style={{ fontSize: '18px', fontWeight: '700', color: 'var(--color-primary)' }}>
                        {formatCurrency(selectedOrder.total)}
                      </span>
                    </div>
                    {(() => {
                      let refunds = [];
                      let totalRefunded = 0;
                      try {
                        const notesHistory = selectedOrder.notes_history
                          ? (typeof selectedOrder.notes_history === 'string'
                            ? JSON.parse(selectedOrder.notes_history)
                            : selectedOrder.notes_history)
                          : [];
                        refunds = notesHistory.filter(note => note.type === 'refund');
                        totalRefunded = refunds.reduce((sum, refund) => sum + (refund.refundAmount || 0), 0);
                      } catch (e) {
                        refunds = [];
                      }

                      if (totalRefunded > 0) {
                        return (
                          <>
                            {refunds.map((refund, index) => (
                              <div key={`refund-summary-${index}`} className="summary-row" style={{ color: 'var(--color-error)' }}>
                                <span>💰 Refund ({formatDateOnlyAtlantic(refund.timestamp)}):</span>
                                <span>- {refund.refundAmountFormatted || `$${((refund.refundAmount || 0) / 100).toFixed(2)} CAD`}</span>
                              </div>
                            ))}
                            <div className="summary-row total" style={{
                              borderTop: '2px solid var(--color-border)',
                              paddingTop: '8px',
                              marginTop: '4px'
                            }}>
                              <span><strong>Net Total:</strong></span>
                              <span><strong style={{ color: 'var(--color-primary)' }}>
                                {formatCurrency(selectedOrder.total - totalRefunded)}
                              </strong></span>
                            </div>
                          </>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              </div>

              <div className="order-collapsible-stack">
                {(() => {
                  const inquiryThread = getAdminInquiryThread(selectedOrder);

                  return (
                    <>
                      <OrderCollapsibleSection
                        title="💬 Conversation"
                        count={inquiryThread.length}
                        highlight={inquiryThread.length > 0}
                        collapsed={isAdminSectionCollapsed(selectedOrder, ADMIN_SECTION_IDS.CONVERSATION)}
                        onToggle={() => toggleAdminSection(selectedOrder, ADMIN_SECTION_IDS.CONVERSATION)}
                      >
                        {inquiryThread.length > 0 ? (
                          <div className="admin-inquiry-thread">
                            {inquiryThread.map((message) => (
                              <div
                                key={message.id}
                                className={`admin-inquiry-message ${message.author}`}
                              >
                                <div className="admin-inquiry-message-header">
                                  <span>
                                    {message.author === 'customer'
                                      ? '👤 Customer'
                                      : `🏢 ${message.author_name || 'Admin'}`}
                                  </span>
                                  <span>{formatDateAtlantic(message.created_at)}</span>
                                </div>
                                <div className="admin-inquiry-message-body">
                                  {message.message}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="order-collapsible-empty">
                            No customer messages yet. Replies will appear here when a customer asks a question.
                          </p>
                        )}

                        <div className="admin-inquiry-reply-form">
                          <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            fontWeight: '600',
                            fontSize: '14px',
                          }}>
                            📝 Reply to Customer:
                          </label>
                          <textarea
                            value={inquiryReply}
                            onChange={(e) => setInquiryReply(e.target.value)}
                            placeholder="Type your reply here... Customer will receive this via email."
                            disabled={submittingReply}
                          />
                          <div className="admin-inquiry-reply-actions">
                            <button
                              onClick={submitInquiryReply}
                              disabled={submittingReply || !inquiryReply.trim()}
                              style={{
                                padding: '10px 20px',
                                backgroundColor: submittingReply || !inquiryReply.trim() ? '#6c757d' : 'var(--color-success)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: submittingReply || !inquiryReply.trim() ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {submittingReply ? '⏳ Sending...' : '📧 Send Reply'}
                            </button>
                          </div>
                        </div>
                      </OrderCollapsibleSection>

                      <OrderCollapsibleSection
                        title="🕘 Order Status History"
                        collapsed={isAdminSectionCollapsed(selectedOrder, ADMIN_SECTION_IDS.HISTORY)}
                        onToggle={() => toggleAdminSection(selectedOrder, ADMIN_SECTION_IDS.HISTORY)}
                      >
                        <OrderStatusTimeline
                          order={selectedOrder}
                          notesHistory={selectedOrder.notes_history}
                          inquiryThread={selectedOrder.inquiry_thread}
                          hideConversationEntries
                          title="Status Updates"
                        />
                      </OrderCollapsibleSection>
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="modal-footer">
              <div className="modal-actions-left">
                <div className="action-group">
                  <span className="action-label">📄 Invoice:</span>
                  <button className="btn btn-action" onClick={handlePrintInvoice} title="Print Invoice">
                    🖨️ Print
                  </button>
                  <button className="btn btn-action" onClick={handleSaveInvoice} title="Save Invoice">
                    💾 Save
                  </button>
                  <button className="btn btn-action" onClick={handleEmailInvoice} title="Email Invoice">
                    📧 Email
                  </button>
                </div>
                <div className="action-group">
                  <span className="action-label">📦 Shipping Label:</span>
                  <button className="btn btn-action" onClick={handlePrintShippingLabel} title="Print Shipping Label">
                    🖨️ Print
                  </button>
                  <button className="btn btn-action" onClick={handleSaveShippingLabel} title="Save Shipping Label">
                    💾 Save
                  </button>
                </div>
                <div className="action-group">
                  <span className="action-label">📧 Customer Follow-up:</span>
                  <button className="btn btn-action" onClick={handleOpenEmailModal} title="Send email to customer / follow-up">
                    ✉️ Send Email
                  </button>
                </div>
                <div className="action-group">
                  <span className="action-label">💰 Refund:</span>
                  <button
                    className="btn btn-action"
                    onClick={handleOpenRefundModal}
                    title="Process refund"
                    disabled={(selectedOrder.payment_status || 'paid') === 'refunded' || selectedOrder.status === 'cancelled'}
                  >
                    💵 Process Refund
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="btn btn-danger"
                  onClick={() => handleDeleteOrder(selectedOrder)}
                  title="Delete this order"
                >
                  {getIcon('delete')} Delete Order
                </button>
                <button className="btn btn-secondary" onClick={handleCloseDetails}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Confirmation Modal */}
      {showStatusConfirmModal && pendingStatusChange && (
        <div className="modal-overlay" onClick={cancelStatusUpdate}>
          <div className="modal-content status-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Status Change</h3>
              <button className="close-btn" onClick={cancelStatusUpdate}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="status-change-info">
                <p>
                  Change <strong>{pendingStatusChange.statusType === 'order' ? 'Order Status (Fulfillment)' : 'Payment Status'}</strong> from <strong className="status-from">{pendingStatusChange.currentStatus}</strong> to <strong className="status-to">{pendingStatusChange.newStatus}</strong>?
                </p>
              </div>

              {/* Order Items Selection */}
              {pendingStatusChange.order && pendingStatusChange.order.items && (
                <div className="order-items-selection">
                  <div className="items-selection-header">
                    <h4>
                      Select Items for this Status Change
                      <span className="selection-count">({selectedItems.length}/{pendingStatusChange.order.items.length} selected)</span>
                    </h4>
                    <span
                      className="select-all-items"
                      onClick={selectedItems.length === pendingStatusChange.order.items.length ? deselectAllItems : selectAllItems}
                    >
                      {selectedItems.length === pendingStatusChange.order.items.length ? '✕ Deselect All' : '✓ Select All'}
                    </span>
                  </div>
                  <div className="items-list-compact confirmation-items">
                    {pendingStatusChange.order.items.map((item, index) => {
                      // Check for bundle in multiple ways
                      const isBundle = item.isBundle || item.is_bundle || false;
                      const isPartOfBundle = item.isPartOfBundle || false;
                      // Also check if bundleProducts array exists (even if isBundle flag is missing)
                      const hasBundleProducts = item.bundleProducts && Array.isArray(item.bundleProducts) && item.bundleProducts.length > 0;
                      const actualIsBundle = isBundle || hasBundleProducts;

                      // Skip individual bundle items - they'll be shown hierarchically under the bundle
                      if (isPartOfBundle && !actualIsBundle) {
                        return null;
                      }

                      // Check if this bundle has expanded products
                      const expandedProducts = pendingStatusChange.order.items.filter(i => i.isPartOfBundle && i.bundleParentId === item.id);
                      const hasExpandedProducts = expandedProducts.length > 0;

                      const bundleProducts = item.bundleProducts || [];
                      const bundleDiscount = item.bundleDiscount || item.bundle_discount || 0;
                      const originalPrice = item.originalPrice || item.original_price || 0;
                      const itemPrice = item.unitPrice || item.price_cents || item.price || 0;

                      // Calculate bundle savings if applicable
                      let bundleSavings = 0;
                      if (actualIsBundle && bundleDiscount > 0 && originalPrice > 0) {
                        bundleSavings = (originalPrice - itemPrice) * item.quantity;
                      }

                      // Use expanded products if available, otherwise use bundleProducts
                      const productsToDisplay = hasExpandedProducts ? expandedProducts : bundleProducts;

                      return (
                        <div
                          key={index}
                          className={`item-row-compact ${selectedItems.includes(index) ? 'selected' : ''}`}
                          style={{
                            backgroundColor: actualIsBundle ? 'color-mix(in srgb, var(--color-primary) 3%, transparent)' : 'transparent',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '12px',
                            border: actualIsBundle ? `2px solid var(--color-primary)` : '1px solid var(--color-border)'
                          }}
                        >
                          <div className="item-checkbox-wrapper">
                            <input
                              type="checkbox"
                              className="item-checkbox"
                              checked={selectedItems.includes(index)}
                              onChange={() => toggleItemSelection(index)}
                            />
                          </div>
                          <div className="item-info" style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: actualIsBundle && productsToDisplay.length > 0 ? '8px' : '0' }}>
                              <strong style={{ color: 'var(--color-text-primary)', fontSize: '15px' }}>
                                {item.name || `Product #${item.id}`}
                              </strong>
                              {actualIsBundle && (
                                <span style={{
                                  background: 'linear-gradient(135deg, var(--color-primary), var(--color-primary-light))',
                                  color: '#ffffff',
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  display: 'inline-block'
                                }}>
                                  📦 BUNDLE
                                </span>
                              )}
                              <span className="item-quantity" style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>
                                × {item.quantity}
                              </span>
                            </div>

                            {/* Variant Display */}
                            {(item.selectedSize || item.selectedColor) && (
                              <div className="item-variants" style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginTop: '4px', fontStyle: 'italic' }}>
                                {item.selectedSize && <span style={{ marginRight: '10px' }}>Size: <strong>{item.selectedSize}</strong></span>}
                                {item.selectedColor && <span>Color: <strong>{item.selectedColor}</strong></span>}
                              </div>
                            )}

                            {/* Hierarchical list of bundle items */}
                            {actualIsBundle && productsToDisplay.length > 0 && (
                              <div style={{
                                marginTop: '8px',
                                paddingLeft: '1.5rem',
                                borderLeft: `2px solid var(--color-primary)`,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px'
                              }}>
                                {productsToDisplay.map((bp, idx) => {
                                  const productName = bp.name || bp.title || `Product #${bp.id || bp.productId || ''}`;
                                  return (
                                    <div key={idx} style={{
                                      fontSize: '13px',
                                      color: 'var(--color-text-secondary)',
                                      lineHeight: '1.5'
                                    }}>
                                      {productName}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {actualIsBundle && bundleSavings > 0 && (
                              <div style={{
                                fontSize: '13px',
                                color: 'var(--color-success)',
                                fontWeight: '600',
                                marginTop: '8px',
                                padding: '6px 10px',
                                backgroundColor: 'color-mix(in srgb, var(--color-success) 10%, transparent)',
                                borderRadius: '6px',
                                display: 'inline-block'
                              }}>
                                🎉 Save {formatCurrency(bundleSavings)} ({bundleDiscount}% off)
                              </div>
                            )}
                          </div>
                          <div className="item-price" style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: actualIsBundle ? 'var(--color-primary)' : 'var(--color-text-primary)',
                            minWidth: '100px',
                            textAlign: 'right'
                          }}>
                            {formatCurrency(itemPrice * item.quantity)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {selectedItems.length === 0 && (
                    <div className="no-items-warning">
                      ⚠️ No items selected. Please select at least one item to proceed.
                    </div>
                  )}
                </div>
              )}

              <div className="email-notification-option">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={sendEmailNotification}
                    onChange={(e) => setSendEmailNotification(e.target.checked)}
                  />
                  <span>Send email notification to customer</span>
                </label>
                <p className="email-hint">
                  Customer will be notified about the order status change via email.
                </p>
              </div>

              {/* Tracking Number (only for shipped status) */}
              {pendingStatusChange.newStatus === 'shipped' && (
                <div className="tracking-number-section" style={{ marginBottom: '1.5rem' }}>
                  <label htmlFor="tracking-number" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.95rem', color: 'var(--color-text-primary)' }}>
                    📦 Tracking Number (Optional)
                  </label>
                  <input
                    id="tracking-number"
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Enter tracking number (e.g., 1Z999AA10123456784)"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid var(--color-border)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'var(--color-surface)',
                      color: 'var(--color-text-primary)',
                      fontFamily: 'monospace',
                      letterSpacing: '0.5px'
                    }}
                  />
                  <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem', fontStyle: 'italic' }}>
                    If provided, the tracking number will be included in the shipping notification email to the customer.
                  </p>
                </div>
              )}

              {/* Custom Message */}
              {sendEmailNotification && (
                <div className="custom-message-section">
                  <label htmlFor="custom-message" className="custom-message-label">
                    <strong>Custom Message (Optional)</strong>
                    <span className="label-hint">Add a personal note to the customer</span>
                  </label>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label htmlFor="canned-message-status" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                      Select Canned Message Template:
                    </label>
                    <select
                      id="canned-message-status"
                      value={selectedCannedMessageForStatus}
                      onChange={(e) => handleCannedMessageSelectForStatus(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid var(--color-border)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        background: 'var(--color-surface)',
                        color: 'var(--color-text-primary)',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">-- Select a template (optional) --</option>
                      {cannedMessages.map((msg) => (
                        <option key={msg.id} value={msg.id}>
                          {msg.label}
                        </option>
                      ))}
                    </select>
                    <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem', fontStyle: 'italic' }}>
                      Select a template to auto-fill the message. You can edit it before sending.
                    </p>
                  </div>

                  <textarea
                    id="custom-message"
                    className="custom-message-textarea"
                    placeholder="e.g., We apologize for the delay in shipping. Item XYZ is temporarily out of stock and we will refund $25 CAD..."
                    value={customMessage}
                    onChange={(e) => {
                      setCustomMessage(e.target.value);
                      // Clear canned selection if user manually edits
                      if (selectedCannedMessageForStatus) {
                        setSelectedCannedMessageForStatus('');
                      }
                    }}
                    rows={14}
                  />
                  <div className="character-count">
                    {customMessage.length} characters
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={cancelStatusUpdate}
                disabled={updatingStatus}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={confirmStatusUpdate}
                disabled={updatingStatus || selectedItems.length === 0}
              >
                {updatingStatus ? 'Updating...' : selectedItems.length === 0 ? 'Select Items First' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Order Confirmation Modal */}
      {showDeleteConfirmModal && orderToDelete && (
        <div className="modal-overlay" onClick={cancelDeleteOrder}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚠️ Confirm Delete Order</h3>
              <button className="close-btn" onClick={cancelDeleteOrder}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="delete-warning">
                <p style={{ fontSize: '1rem', marginBottom: '1rem' }}>
                  Are you sure you want to delete this order?
                </p>
                <div className="order-delete-details">
                  <div><strong>Order Number:</strong> {orderToDelete.order_number}</div>
                  <div><strong>Customer:</strong> {orderToDelete.customer_name}</div>
                  <div><strong>Email:</strong> {orderToDelete.customer_email}</div>
                  <div><strong>Total:</strong> {formatCurrency(orderToDelete.total)}</div>
                  <div><strong>Status:</strong> <span className={getStatusBadgeClass(orderToDelete.status)}>{orderToDelete.status}</span></div>
                </div>
                <p style={{ color: '#dc2626', fontWeight: 'bold', marginTop: '1rem' }}>
                  ⚠️ This action cannot be undone!
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={cancelDeleteOrder}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmDeleteOrder}
              >
                {getIcon('delete')} Yes, Delete Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowRefundModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '620px' }}>
            <div className="modal-header">
              <h3>💰 Process Refund</h3>
              <button className="close-btn" onClick={() => setShowRefundModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Info Box - Partial Refunds Supported */}
              <div style={{
                padding: '1rem',
                background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                border: '2px solid #0284c7',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '1.5rem' }}>💡</span>
                  <div>
                    <p style={{ fontSize: '0.95rem', fontWeight: '600', color: '#075985', margin: '0 0 0.5rem 0' }}>
                      Partial & Full Refunds Supported
                    </p>
                    <p style={{ fontSize: '0.85rem', color: '#0c4a6e', margin: 0, lineHeight: 1.5 }}>
                      Both Stripe and PayPal support partial refunds. Enter any amount up to the order total,
                      or the full amount for a complete refund. The customer will be notified automatically.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.9rem', color: '#666' }}>
                  Order: <strong>{selectedOrder.order_number}</strong>
                </p>
                <p style={{ fontSize: '0.9rem', color: '#666' }}>
                  Payment Method: <strong>{selectedOrder.payment_method}</strong>
                </p>
                <p style={{ fontSize: '0.9rem', color: '#666' }}>
                  Order Total: <strong>${(selectedOrder.total / 100).toFixed(2)} CAD</strong>
                </p>
                {selectedOrder.status === 'partially_refunded' && (
                  <p style={{ fontSize: '0.9rem', color: '#f97316', fontWeight: '600' }}>
                    ⚠️ This order has already been partially refunded
                  </p>
                )}
              </div>

              {/* Refund Mode Selection */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Refund Method:
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setRefundMode('amount');
                      setSelectedRefundItems([]);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      fontSize: '14px',
                      background: refundMode === 'amount' ? 'var(--color-primary)' : '#e5e7eb',
                      color: refundMode === 'amount' ? 'white' : 'var(--color-text-primary)',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: refundMode === 'amount' ? '600' : '400'
                    }}
                  >
                    💵 Enter Amount
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setRefundMode('items');
                      setRefundAmount('0.00');
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      fontSize: '14px',
                      background: refundMode === 'items' ? 'var(--color-primary)' : '#e5e7eb',
                      color: refundMode === 'items' ? 'white' : 'var(--color-text-primary)',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: refundMode === 'items' ? '600' : '400'
                    }}
                  >
                    📦 Select Items
                  </button>
                </div>
              </div>

              {/* Item Selection Mode */}
              {refundMode === 'items' && (
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Select Items to Refund:
                  </label>
                  <div style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    padding: '0.5rem'
                  }}>
                    {selectedOrder.items && selectedOrder.items.length > 0 ? (
                      selectedOrder.items.map((item, index) => {
                        // Skip items that are part of a bundle (they're already included in bundle price)
                        if (item.isPartOfBundle && !item.isBundle) return null;

                        const itemPrice = item.unitPrice || item.price_cents || item.price || 0;
                        const quantity = item.quantity || 1;
                        const itemTotal = (itemPrice * quantity) / 100;
                        const isSelected = selectedRefundItems.includes(index);

                        return (
                          <div
                            key={index}
                            onClick={() => toggleRefundItem(index)}
                            style={{
                              padding: '0.75rem',
                              marginBottom: '0.5rem',
                              border: `2px solid ${isSelected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                              borderRadius: '6px',
                              backgroundColor: isSelected ? 'color-mix(in srgb, var(--color-primary) 10%, transparent)' : 'transparent',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleRefundItem(index)}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer'
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', color: 'var(--color-text-primary)' }}>
                                {item.name || `Product #${item.id}`}
                              </div>
                              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                                × {quantity} @ ${(itemPrice / 100).toFixed(2)} = ${itemTotal.toFixed(2)} CAD
                              </div>
                            </div>
                            {isSelected && (
                              <span style={{ color: 'var(--color-primary)', fontWeight: '600' }}>✓</span>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <p style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                        No items found in this order
                      </p>
                    )}
                  </div>
                  {selectedRefundItems.length > 0 && (
                    <p style={{ fontSize: '0.875rem', color: '#059669', marginTop: '0.5rem', fontWeight: '500' }}>
                      ✓ {selectedRefundItems.length} item{selectedRefundItems.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              )}

              {/* Amount Input Mode */}
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label htmlFor="refund-amount" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Refund Amount ($ CAD):
                </label>
                <input
                  id="refund-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={(selectedOrder.total / 100).toFixed(2)}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder="Enter amount or full amount"
                  disabled={refundMode === 'items'}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    opacity: refundMode === 'items' ? 0.6 : 1,
                    cursor: refundMode === 'items' ? 'not-allowed' : 'text'
                  }}
                />
                {refundMode === 'amount' && (
                  <>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={() => setRefundAmount((selectedOrder.total / 100 / 2).toFixed(2))}
                        style={{
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.75rem',
                          background: '#e5e7eb',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        50%
                      </button>
                      <button
                        type="button"
                        onClick={() => setRefundAmount((selectedOrder.total / 100).toFixed(2))}
                        style={{
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.75rem',
                          background: '#e5e7eb',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        100% (Full)
                      </button>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: '#059669', marginTop: '0.5rem', fontWeight: '500' }}>
                      ✓ Max: ${(selectedOrder.total / 100).toFixed(2)} CAD
                    </p>
                  </>
                )}
                {refundMode === 'items' && (
                  <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    Amount calculated from selected items above
                  </p>
                )}
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label htmlFor="refund-reason" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Reason:
                </label>
                <select
                  id="refund-reason"
                  value={refundReason}
                  onChange={(e) => {
                    const nextReason = e.target.value;
                    setRefundReason(nextReason);
                    applyRefundReasonTemplate(nextReason, refundAmount, selectedOrder);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    cursor: 'pointer'
                  }}
                >
                  <option value="shipping_refund">Shipping Refund</option>
                  <option value="requested_by_customer">Requested by Customer</option>
                  <option value="duplicate">Duplicate</option>
                  <option value="fraudulent">Fraudulent</option>
                </select>
                <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', margin: '0.4rem 0 0' }}>
                  Selecting a reason loads a canned explanation you can edit before sending.
                </p>
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '0.5rem' }}>
                  <label htmlFor="refund-explanation" style={{ fontWeight: '500', margin: 0 }}>
                    Explanation (will be included in email):
                  </label>
                  <button
                    type="button"
                    onClick={() => applyRefundReasonTemplate(refundReason, refundAmount, selectedOrder)}
                    style={{
                      padding: '0.35rem 0.65rem',
                      fontSize: '0.75rem',
                      background: '#e5e7eb',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    title="Reload the canned template for the selected reason (uses current refund amount)"
                  >
                    📋 Apply template
                  </button>
                </div>
                <textarea
                  id="refund-explanation"
                  value={refundExplanation}
                  onChange={(e) => {
                    setRefundExplanationDirty(true);
                    setRefundExplanation(e.target.value);
                  }}
                  placeholder="Enter an explanation for the refund (optional but recommended)"
                  rows={8}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{
                padding: '1rem',
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <p style={{ fontSize: '0.9rem', color: '#856404', margin: 0 }}>
                  ⚠️ <strong>Warning:</strong> This action cannot be undone. The refund will be processed immediately through {selectedOrder.payment_method}.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowRefundModal(false);
                  setRefundAmount('');
                  setRefundExplanation('');
                }}
                disabled={processingRefund}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleProcessRefund}
                disabled={processingRefund || !refundAmount || parseFloat(refundAmount) <= 0 || (refundMode === 'items' && selectedRefundItems.length === 0)}
              >
                {processingRefund ? 'Processing...' : '💰 Process Refund'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Confirmation Modal */}
      {showRefundConfirmModal && selectedOrder && (
        <div className="modal-overlay" onClick={cancelRefund}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>⚠️ Confirm Refund</h3>
              <button className="close-btn" onClick={cancelRefund}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="delete-warning">
                <p style={{ fontSize: '1rem', marginBottom: '1rem', fontWeight: '600' }}>
                  Are you sure you want to process this refund?
                </p>
                <div className="order-delete-details" style={{
                  background: 'var(--color-surface)',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  <div><strong>Order Number:</strong> {selectedOrder.order_number}</div>
                  <div><strong>Customer:</strong> {selectedOrder.customer_name}</div>
                  <div><strong>Payment Method:</strong> {selectedOrder.payment_method}</div>
                  <div><strong>Refund Amount:</strong> <span style={{ color: '#dc2626', fontWeight: '600', fontSize: '1.1rem' }}>${refundAmount} CAD</span></div>
                  <div><strong>Original Total:</strong> ${(selectedOrder.total / 100).toFixed(2)} CAD</div>
                  {refundExplanation && (
                    <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--color-border)' }}>
                      <strong>Explanation:</strong>
                      <div style={{
                        marginTop: '0.25rem',
                        padding: '0.5rem',
                        background: 'var(--color-background)',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        fontStyle: 'italic',
                        color: 'var(--color-text-secondary)'
                      }}>
                        {refundExplanation}
                      </div>
                    </div>
                  )}
                </div>
                <p style={{ color: '#dc2626', fontWeight: 'bold', marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  ⚠️ This action cannot be undone! The refund will be processed immediately.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={cancelRefund}
                disabled={processingRefund}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmRefund}
                disabled={processingRefund}
              >
                {processingRefund ? 'Processing Refund...' : '💰 Confirm Refund'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Success Modal */}
      {showRefundSuccessModal && selectedOrder && refundSuccessResult && (
        <div className="modal-overlay" onClick={closeRefundSuccessModal}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <div className="modal-header">
              <h3>✅ Refund Processed</h3>
              <button className="close-btn" onClick={closeRefundSuccessModal}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <p style={{ fontSize: '1rem', marginBottom: '1rem', color: 'var(--color-success, #16a34a)', fontWeight: '600' }}>
                {refundSuccessResult.message || 'Refund processed successfully'}
              </p>
              <div className="order-delete-details" style={{
                background: 'var(--color-surface)',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1rem',
              }}>
                <div><strong>Order:</strong> {refundSuccessResult.orderNumber || selectedOrder.order_number}</div>
                <div><strong>Refund amount:</strong> <span style={{ color: '#dc2626', fontWeight: '600' }}>${((refundSuccessResult.amount || 0) / 100).toFixed(2)} CAD</span></div>
                <div><strong>Refund ID:</strong> {refundSuccessResult.refundId || '—'}</div>
                <div><strong>Payment status:</strong> {(refundSuccessResult.paymentStatus || selectedOrder.payment_status || 'paid').replace(/_/g, ' ')}</div>
                <div><strong>Total refunded on order:</strong> ${((refundSuccessResult.totalRefunded || 0) / 100).toFixed(2)} CAD</div>
                <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--color-border)' }}>
                  <div><strong>Customer email</strong> ({refundSuccessResult.customerEmail || selectedOrder.customer_email}): {refundSuccessResult.customerEmailSent ? '✅ Sent' : '⚠️ Not sent'}</div>
                  <div><strong>Admin confirmation email:</strong> {refundSuccessResult.adminEmailSent ? '✅ Sent' : '⚠️ Not sent'}</div>
                </div>
              </div>
              <p style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)', margin: 0 }}>
                The invoice now includes refund line items. You can print or email the updated invoice to the customer.
              </p>
            </div>

            <div className="modal-footer" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
              <button className="btn btn-secondary" onClick={closeRefundSuccessModal}>
                Close
              </button>
              <button className="btn btn-secondary" onClick={printUpdatedInvoice}>
                🖨️ Print Updated Invoice
              </button>
              <button className="btn btn-primary" onClick={emailUpdatedInvoice}>
                📧 Email Updated Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Follow-up Modal */}
      {showEmailModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => {
          setShowEmailModal(false);
          setSelectedCannedMessage('');
        }}>
          <div className="modal-content email-followup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📧 Send Follow-up Email</h3>
              <button className="close-btn" onClick={() => {
                setShowEmailModal(false);
                setSelectedCannedMessage('');
              }}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.9rem', color: '#666' }}>
                  Sending to: <strong>{selectedOrder.customer_email}</strong>
                </p>
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label htmlFor="canned-message" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Select Canned Message (Optional):
                </label>
                <select
                  id="canned-message"
                  value={selectedCannedMessage}
                  onChange={(e) => handleCannedMessageSelect(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">-- Select a template --</option>
                  {cannedMessages.map((msg) => (
                    <option key={msg.id} value={msg.id}>
                      {msg.label}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem', fontStyle: 'italic' }}>
                  Select a template to auto-fill the subject and message. You can edit it before sending.
                </p>
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label htmlFor="email-subject" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Subject:
                </label>
                <input
                  id="email-subject"
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label htmlFor="email-message" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Message:
                </label>
                <textarea
                  id="email-message"
                  className="custom-message-textarea"
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Enter your message to the customer..."
                  rows={14}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailSubject('');
                  setEmailMessage('');
                  setSelectedCannedMessage('');
                }}
                disabled={sendingEmail}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSendFollowUpEmail}
                disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
              >
                {sendingEmail ? 'Sending...' : '📧 Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedCartInquiry && (
        <div className="modal-overlay" onClick={() => setSelectedCartInquiry(null)}>
          <div className="modal-content cart-inquiry-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                📧 Cart inquiry {selectedCartInquiry.reference_id}
                {isCartInquiryArchived(selectedCartInquiry) ? ' · archived' : ''}
              </h3>
              <button type="button" className="close-btn" onClick={() => setSelectedCartInquiry(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p>
                <strong>{selectedCartInquiry.customer_name || 'Customer'}</strong>
                {' · '}
                {selectedCartInquiry.customer_email}
                {selectedCartInquiry.customer_phone ? ` · ${selectedCartInquiry.customer_phone}` : ''}
              </p>
              <p className="cart-inquiry-detail-meta">
                {formatDateWithTime(selectedCartInquiry)} · estimated {formatCurrency(selectedCartInquiry.total)}
              </p>
              {selectedCartInquiry.personal_message && (
                <div className="cart-inquiry-message-block">
                  <h4>Customer message</h4>
                  <p>{selectedCartInquiry.personal_message}</p>
                </div>
              )}
              <h4>Items</h4>
              <ul className="cart-inquiry-item-list">
                {(selectedCartInquiry.items || []).map((item, index) => {
                  const itemPrice = item.unitPrice || item.price_cents || item.price || 0;
                  return (
                    <li key={`${item.id}-${index}`}>
                      <span>
                        {item.name || `Product #${item.id}`}
                        {' '}×{item.quantity}
                      </span>
                      <span>{formatCurrency(itemPrice * item.quantity)}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="cart-inquiry-totals">
                <div><span>Subtotal</span><span>{formatCurrency(selectedCartInquiry.subtotal)}</span></div>
                <div><span>Shipping</span><span>{selectedCartInquiry.shipping === 0 ? 'FREE' : formatCurrency(selectedCartInquiry.shipping)}</span></div>
                <div><span>Tax</span><span>{formatCurrency(selectedCartInquiry.tax)}</span></div>
                <div className="cart-inquiry-totals-total"><span>Estimated total</span><span>{formatCurrency(selectedCartInquiry.total)}</span></div>
              </div>
              {formatAddressForMailOrder(selectedCartInquiry.shipping_address) && (
                <div className="cart-inquiry-shipping">
                  <h4>Shipping address</h4>
                  <pre>{formatAddressForMailOrder(selectedCartInquiry.shipping_address)}</pre>
                </div>
              )}
              {getCartInquiryReplies(selectedCartInquiry).length > 0 && (
                <div className="cart-inquiry-replies">
                  <h4>Replies</h4>
                  {getCartInquiryReplies(selectedCartInquiry).map((reply) => (
                    <div key={reply.id || reply.created_at} className="cart-inquiry-reply-item">
                      <div className="cart-inquiry-reply-meta">
                        {reply.author_name || 'Admin'}
                        {reply.created_at ? ` · ${formatOrderDate(reply.created_at)}` : ''}
                        {reply.sentViaEmail ? ' · emailed' : ' · note only'}
                      </div>
                      <p>{reply.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline"
                disabled={archivingCartInquiryId === selectedCartInquiry.id}
                onClick={() => archiveCartInquiry(selectedCartInquiry, !isCartInquiryArchived(selectedCartInquiry))}
              >
                {getIcon('archive')} {isCartInquiryArchived(selectedCartInquiry) ? 'Restore' : 'Archive'}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  setCartInquiryToDelete(selectedCartInquiry);
                  setSelectedCartInquiry(null);
                }}
              >
                {getIcon('delete')} Delete
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setSelectedCartInquiry(null)}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => openCartInquiryReply(selectedCartInquiry)}
              >
                💬 Reply
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => openMailOrderFromInquiry(selectedCartInquiry)}
              >
                Create mail order
              </button>
            </div>
          </div>
        </div>
      )}

      {showCartInquiryReplyModal && cartInquiryReplyTarget && (
        <div className="modal-overlay" onClick={closeCartInquiryReply}>
          <div className="modal-content status-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reply to cart inquiry</h3>
              <button type="button" className="close-btn" onClick={closeCartInquiryReply} disabled={sendingCartInquiryReply}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="status-change-info">
                <p>
                  Reply to <strong>{cartInquiryReplyTarget.customer_name || 'Customer'}</strong>
                  {' '}({cartInquiryReplyTarget.customer_email}) about
                  {' '}<strong>{cartInquiryReplyTarget.reference_id}</strong>.
                </p>
              </div>

              <div className="email-notification-option">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={cartInquirySendEmail}
                    onChange={(e) => setCartInquirySendEmail(e.target.checked)}
                    disabled={sendingCartInquiryReply}
                  />
                  <span>Send email notification to customer</span>
                </label>
                <p className="email-hint">
                  Customer will be notified about this cart inquiry via email.
                </p>
              </div>

              <div className="custom-message-section">
                <label htmlFor="cart-inquiry-custom-message" className="custom-message-label">
                  <strong>Custom Message (Optional)</strong>
                  <span className="label-hint">Add a personal note to the customer</span>
                </label>

                <div style={{ marginBottom: '0.75rem' }}>
                  <label htmlFor="canned-message-cart-inquiry" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                    Select Canned Message Template:
                  </label>
                  <select
                    id="canned-message-cart-inquiry"
                    value={selectedCannedMessageForCartInquiry}
                    onChange={(e) => handleCannedMessageSelectForCartInquiry(e.target.value)}
                    disabled={sendingCartInquiryReply}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid var(--color-border)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: 'var(--color-surface)',
                      color: 'var(--color-text-primary)',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">-- Select a template (optional) --</option>
                    {cannedMessages.map((msg) => (
                      <option key={msg.id} value={msg.id}>
                        {msg.label}
                      </option>
                    ))}
                  </select>
                  <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem', fontStyle: 'italic' }}>
                    Select a template to auto-fill the message. You can edit it before sending.
                  </p>
                </div>

                <textarea
                  id="cart-inquiry-custom-message"
                  className="custom-message-textarea"
                  placeholder="e.g., Thanks for sending your cart — we can put this together as a mail / check order..."
                  value={cartInquiryReplyMessage}
                  onChange={(e) => {
                    setCartInquiryReplyMessage(e.target.value);
                    if (selectedCannedMessageForCartInquiry) {
                      setSelectedCannedMessageForCartInquiry('');
                    }
                  }}
                  disabled={sendingCartInquiryReply}
                  rows={14}
                />
                <div className="character-count">
                  {cartInquiryReplyMessage.length} characters
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeCartInquiryReply}
                disabled={sendingCartInquiryReply}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmCartInquiryReply}
                disabled={sendingCartInquiryReply}
              >
                {sendingCartInquiryReply
                  ? 'Sending...'
                  : cartInquirySendEmail
                    ? '📧 Send Reply Email'
                    : 'Save Reply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {cartInquiryToDelete && (
        <div className="modal-overlay" onClick={() => !deletingCartInquiry && setCartInquiryToDelete(null)}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚠️ Confirm Delete Cart Inquiry</h3>
              <button
                type="button"
                className="close-btn"
                onClick={() => setCartInquiryToDelete(null)}
                disabled={deletingCartInquiry}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-warning">
                <p style={{ fontSize: '1rem', marginBottom: '1rem' }}>
                  Are you sure you want to delete this emailed cart?
                </p>
                <div className="order-delete-details">
                  <div><strong>Reference:</strong> {cartInquiryToDelete.reference_id}</div>
                  <div><strong>Customer:</strong> {cartInquiryToDelete.customer_name || '—'}</div>
                  <div><strong>Email:</strong> {cartInquiryToDelete.customer_email}</div>
                  <div><strong>Total:</strong> {formatCurrency(cartInquiryToDelete.total)}</div>
                </div>
                <p style={{ color: '#dc2626', fontWeight: 'bold', marginTop: '1rem' }}>
                  ⚠️ This action cannot be undone!
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setCartInquiryToDelete(null)}
                disabled={deletingCartInquiry}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDeleteCartInquiry}
                disabled={deletingCartInquiry}
              >
                {getIcon('delete')} {deletingCartInquiry ? 'Deleting...' : 'Yes, Delete Inquiry'}
              </button>
            </div>
          </div>
        </div>
      )}

      <AdminMailOrderModal
        isOpen={showMailOrderModal}
        initialDraft={mailOrderDraft}
        onClose={() => {
          setShowMailOrderModal(false);
          setMailOrderDraft(null);
        }}
        onOrderCreated={handleMailOrderCreated}
        emailInvoiceForOrder={handleEmailMailOrderInvoice}
      />
    </div>
  );
};

export default AdminOrders;

